<script setup>
import { computed, ref, watch, provide, nextTick, reactive } from 'vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import DocumentoEntradaItensSmartMode from '@/components/Compras/documentoEntrada/DocumentoEntradaItensSmartMode.vue'
import FiscalPreviewCard from '@/components/base/fiscal/FiscalPreviewCard.vue'
import { criarConfigBuscaDocumentoEntradaTipo } from '@/search-definitions/documentoEntradaTipoSearch'
import { criarConfigBuscaFornecedorDocumentoEntrada } from '@/search-definitions/fornecedorSearch'
import { criarConfigBuscaEnderecoParceiro } from '@/search-definitions/parceiroEnderecoSearch'
import { criarConfigBuscaOperacaoCompra } from '@/search-definitions/operacaoCompraSearch'
import { criarConfigBuscaCondicaoPagamento } from '@/search-definitions/condicaoPagamentoSearch'
import { filialSearch } from '@/search-definitions/filialSearch'
import { criarConfigBuscaLocalEstoque } from '@/search-definitions/localEstoqueSearch'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'
import { GEO_UF } from '@/constants/GEO_UF'
import { SUP_ST_DOC_ENT } from '@/constants/SUP_ST_DOC_ENT'
import { TP_MOVIMENTO } from '@/constants/TP_MOVIMENTO'
import { OC_STATUS } from '@/constants/OC_STATUS'
import { DOC_ENT_LOCAL } from '@/constants/DOC_ENT_LOCAL'
import { MOE } from '@/constants/MOE'
import { useDocumentoEntradaAcoes } from '@/composables/useDocumentoEntradaAcoes'

const authStore = useAuthStore()

const nivelFiscal = computed(() => authStore.lstNivelModuloFiscal)

const modelValueComputed = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  modo: { type: String, default: 'criacao' }, // 'criacao' | 'edicao' | 'visualizacao'
  documento: { type: Object, default: null },
  idFilial: { type: Number, default: null },
  lstMoeda: { type: String, default: '' },
  idOperacao: { type: Number, default: null },
  idParceiroNegocioEmissor: { type: Number, default: null },
  isInterno: { type: Boolean, default: false },
  idPurchaseOrder: { type: Number, default: null },
  idOrganizacao: { type: Number, default: null },
  listas: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  lstTipoTaxaConversao: { type: String, default: null },
  approvalConfig: { type: Object, default: null },
  // === NOVAS PROPS (origem do documento) ===
  idOrdemCompra: { type: Number, default: null },
  idEmbarque: { type: Number, default: null },
  idEmbarqueContainer: { type: Number, default: null },
  // === SMART MODE ===
  comSmartMode: { type: Boolean, default: false },
  habilitarParceiroProdutor: { type: Boolean, default: false },
  configBuscaLoteFabricacao: { type: Object, default: () => ({}) },
  idParceiroNegocioFornecedor: { type: Number, default: null },
  contextoOrigem: { type: String, default: 'outros' },
  // === MODO DE ALOCAÇÃO DO SMART MODE ===
  /// <summary>'saldo' = alocação via lista de saldos; 'livre' = via cadastro de produtos.
  /// Definido explicitamente pelo componente pai.</summary>
  modoAlocacao: { type: String, default: 'livre' },
  // === LOCAL DE ESTOQUE (seleção na etapa Identificação, conforme operação) ===
  lstTiposLocalEstoque: { type: Array, default: () => [] },
  /// <summary>Valor padrão para IdLocalEstoque, propagado do cabeçalho da OC.
  /// Quando preenchido, preenche automaticamente o campo Local de Estoque no documento
  /// e é repassado ao smartmode como preset para novos itens.</summary>
  idLocalEstoquePadrao: { type: Number, default: null },
  // === CONDIÇÃO E FORMA DE PAGAMENTO (propagados do cabeçalho da OC) ===
  idCondicaoPagamento: { type: Number, default: null },
  lstFormaPagamento: { type: String, default: null },
  lstFormaPagamentoInternacional: { type: String, default: null },
  // === STATUS DA ORDEM DE COMPRA (validação para vincular documentos) ===
  lstStatusOrdemCompra: { type: String, default: null },
  // === LOCAL DO DOCUMENTO (origem sistêmica) ===
  lstLocalDocumento: { type: String, default: null }
})

const emit = defineEmits([
  'update:modelValue',
  'salvar',
  'salvar-intermediario',
  'show-notification',
  'cancelar-aprovacao',
  'alocar-item',
  'atualizar-item',
  'remover-item'
])

// ============================================================
// VALIDAÇÃO FRONTEND (provide/inject)
// ============================================================
const formErrors = ref({})
provide('formErrors', formErrors)

const metadataStore = useMetadataListasStore()

// ============================================================
// MAPA DE CAMPOS OBRIGATÓRIOS POR STEP (validação condicional)
// ============================================================
const camposObrigatoriosPorStep = [
  // Step 0 — Identificação
  [
    { id: 'idFilial', label: 'Filial', condicao: () => true },
    { id: 'idTipoDocumentoEntrada', label: 'Tipo de Documento', condicao: () => true },
    { id: 'idOperacao', label: 'Operação', condicao: () => nivelFiscal.value !== NV_MODULO_FISCAL.Essential },
    { id: 'idLocalEstoque', label: 'Local de Estoque', condicao: () => operacaoSelecionada.value?.movimentaEstoque === true }
  ],
  // Step 1 — Fornecedor
  [
    { id: 'idParceiroNegocioEmissor', label: 'Fornecedor Emissor', condicao: () => true },
    { id: 'idParceiroEnderecoFaturamento', label: 'Endereço de Faturamento', condicao: () => !!formData.value?.idParceiroNegocioEmissor },
    { id: 'idParceiroEnderecoExpedicao', label: 'Endereço de Expedição', condicao: () => !!formData.value?.idParceiroNegocioEmissor }
  ],
  // Step 2 — Dados do Documento
  [
    { id: 'numeroDocumento', label: 'Nº Documento', condicao: () => !formData.value?.isInterno },
    { id: 'dataEmissao', label: 'Data de Emissão', condicao: () => !formData.value?.isInterno },
    { id: 'lstMoeda', label: 'Moeda', condicao: () => true },
    { id: 'lstTipoTaxaConversao', label: 'Tipo Taxa Conversão', condicao: () => modoAprovacao.value && !!camposPendentes.value?.lstTipoTaxaConversao }
  ],
  // Step 3 — Itens (validado pelo componente filho)
  [],
  // Step 4 — Fiscal
  [
    { id: 'idPerfilTributario', label: 'Perfil Tributário', condicao: () => nivelFiscal.value === NV_MODULO_FISCAL.Essential }
  ],
  // Step 5 — Modo Pagamento (validado pelas regras de parcelas)
  []
]

const validarCamposObrigatoriosFrontEnd = () => {
  // Preserva o proxy reativo — NUNCA reatribui o objeto inteiro
  for (const key in formErrors.value) {
    delete formErrors.value[key]
  }

  let isValid = true
  let primeiroStepComErro = -1

  camposObrigatoriosPorStep.forEach((campos, stepIndex) => {
    campos.forEach(campo => {
      if (!campo.condicao()) return

      const valor = formData.value?.[campo.id]
      const isEmpty = valor === null || valor === undefined || valor === '' ||
        (typeof valor === 'number' && isNaN(valor))

      if (isEmpty) {
        formErrors.value[campo.id] = `O campo "${campo.label}" é de preenchimento obrigatório.`
        isValid = false
        if (primeiroStepComErro === -1 || stepIndex < primeiroStepComErro) {
          primeiroStepComErro = stepIndex
        }
      }
    })
  })

  // Se encontrou erros em steps anteriores, navega para o primeiro step com erro
  if (!isValid && primeiroStepComErro >= 0 && primeiroStepComErro !== currentStep.value) {
    currentStep.value = primeiroStepComErro
  }

  return isValid
}

/**
 * Valida cada step do stepper assim que o modal é aberto, marcando como
 * "completed" (verde + ícone check) os steps que já estão preenchidos.
 * Reusa a mesma lógica de camposObrigatoriosPorStep para consistência.
 *
 * Regras:
 * - Step 4 (Fiscal): é apenas um step de relatório, NUNCA é marcado como completed aqui
 * - Steps bloqueados (não acessíveis): NUNCA são marcados como completed,
 *   mesmo que os campos estejam preenchidos
 */
const validarStepsAoAbrir = () => {
  if (!formData.value) return

  steps.forEach((step, index) => {
    // Step 4 (Fiscal): fica verde quando há itens e o fiscal já foi calculado
    // (simulação agregada em digitação ou dados fiscais persistidos de documento aprovado)
    if (index === 4) {
      const temItens = (formData.value?.itens?.length || 0) > 0
      const temFiscal = !!simulacaoDocumentoResultado.value || !!fiscalDataPersistido.value
      step.completed = temItens && temFiscal
      return
    }

    // Steps bloqueados (não acessíveis): não podem aparecer como validados
    if (!isStepAccessible(index)) {
      step.completed = false
      return
    }

    const campos = camposObrigatoriosPorStep[index]
    if (!campos) {
      step.completed = false
      return
    }

    let stepValido = true
    for (const campo of campos) {
      if (!campo.condicao()) continue
      const valor = formData.value?.[campo.id]
      const isEmpty = valor === null || valor === undefined || valor === '' ||
        (typeof valor === 'number' && isNaN(valor))
      if (isEmpty) {
        stepValido = false
        break
      }
    }

    // Step 3 (Itens): validado pelo componente filho — considera válido se houver itens
    if (index === 3) {
      stepValido = (formData.value?.itens?.length || 0) > 0
    }

    // Step 5 (Modo Pagamento):
    // - Documento aprovado: considera válido se houver parcelas carregadas do banco
    // - Documento em digitação: validado pelas regras de parcelas (consistentes, sem erros)
    if (index === 5) {
      if (formData.value?.lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado) {
        stepValido = (parcelas.value?.length || 0) > 0
      } else {
        stepValido = parcelasProntasParaAprovacao.value
      }
    }

    step.completed = stepValido
  })
}

const focarPrimeiroErro = async () => {
  await nextTick()
  setTimeout(() => {
    const elementoComErro = document.querySelector('.is-invalid')
    if (elementoComErro) {
      elementoComErro.focus()
      elementoComErro.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, 50)
}

// ============================================================
// REGRAS DE ACESSO (RBAC)
// ============================================================
const temDocumentoEntradaCompleto = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('DOCUMENTO_ENTRADA_COMPLETO')) return true
  return authStore.getPermsDoModulo('DOCUMENTO_ENTRADA').some(p =>
    p === 'COMPLETO' || (p.includes('COMPLETO') && !p.includes('PADRAO'))
  )
})

const isVisualizacao = computed(() => props.modo === 'visualizacao')
const podeEditar = computed(() => !isVisualizacao.value)

// ============================================================
// COMPUTED: exibe botão de salvamento intermediário para liberar smartmode
// ============================================================
const exibirBotaoSalvarSmartMode = computed(() => {
  return props.comSmartMode &&
    podeEditar.value &&
    formData.value &&
    !formData.value.idDocumentoEntrada
})

const isCadastroValido = computed(() => {
  if (!formData.value) return false
  if (nivelFiscal.value === NV_MODULO_FISCAL.Essential) {
    return !!formData.value.idPerfilTributario
  }
  return !!formData.value.idOperacao
})

// ============================================================
// MODO DE ALOCAÇÃO DO SMART MODE (saldo vs. livre)
// Definido explicitamente pelo componente pai via prop `modoAlocacao`:
// - 'saldo': alocação via lista de saldos (cota, OC, PurchaseOrder)
// - 'livre': alocação via cadastro de produtos da base
// ============================================================
const modoAlocacaoSmartMode = computed(() => props.modoAlocacao)

// ============================================================
// REGIME DA OPERAÇÃO (NACIONAL vs IMPORTAÇÃO/EXPORTAÇÃO)
// Determinado pelo CFOP Exterior da Operação selecionada no documento.
// Se a Operação possui CfopExterior preenchido, é importação/exportação.
// Usado para auto-preenchimento do País de Origem (Brasil se nacional).
// ============================================================
const ehOperacaoNacional = computed(() => {
  return !isExportacao.value
})

// ============================================================
// EXIBIÇÃO DA MODALIDADE DE RATEIO NO SMARTMODE
// Baseada no LOCAL DE ORIGEM do documento (DOC_ENT_LOCAL), não no regime.
// A modalidade de rateio só é oculta quando o documento é de Ordem de Compra,
// pois o rateio nacional de OC é sempre por ValorTotalLinha (FISC_MOD_RATEIO4).
// Documentos de Embarque/Container/PurchaseOrder etc. sempre exibem,
// independente de a operação ser nacional ou importação.
// ============================================================
const exibirModalidadeRateio = computed(() => {
  return formData.value?.lstLocalDocumento !== DOC_ENT_LOCAL.OrdemCompra
})

// ============================================================
// CONTEXTO FISCAL PARA PRÉVIA DOS IMPOSTOS (enviado ao filho)
// ============================================================
const filialSelecionada = ref(null)
const fornecedorData = ref(null)
const operacaoSelecionada = ref(null)

// Regime da operação: true se a operação possui CfopExterior (importação/exportação)
const isExportacao = computed(() => {
  return operacaoSelecionada.value?.cfopExterior && operacaoSelecionada.value.cfopExterior.length > 0
})

const ufParaCodigo = (uf) => {
  if (!uf) return null
  const upper = uf.toUpperCase()
  if (upper.length === 2) return GEO_UF[upper] || upper
  return upper
}

const fiscalPreviewContext = computed(() => {
  if (!formData.value) return null
  const filial = filialSelecionada.value
  const forn = fornecedorData.value
  return {
    idOperacao: formData.value.idOperacao || null,
    lstUfOrigem: forn?.ufOrigem || null,
    lstUfDestino: filial ? ufParaCodigo(filial.uf) : null,
    codigoIbge: filial?.codigoMunicipioIbge || null,
    tipoPessoaParceiro: forn?.lstTipoPessoa || null,
    regimeTributarioParceiro: forn?.lstRegimeTributario || null,
    isOrgaoGovernoParceiro: !!forn?.isOrgaoGoverno,
    lstTipoMovimento: operacaoSelecionada.value?.lstTipoMovimento || TP_MOVIMENTO.Entrada
  }
})

// ============================================================
// DADOS FISCAIS PERSISTIDOS (agregados dos itens)
// ============================================================
const fiscalDataPersistido = computed(() => {
  const itens = formData.value?.itens
  if (!itens || itens.length === 0) return null
  const temFiscal = itens.some(i => i.valorIcmsCalculado != null)
  if (!temFiscal) return null
  // Usa apenas ICMS Efetivo (já inclui o nominal quando não há incentivo) — NÃO duplica com ICMS nominal
  const valorIcmsExibicao = itens.reduce((s, i) => s + (Number(i.valorIcmsEfetivoCalculado) || 0), 0)
  const totalImpostos =
    valorIcmsExibicao +
    itens.reduce((s, i) => s + (Number(i.valorPisCalculado) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorCofinsCalculado) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorDifalCalculado) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorIeCalculado) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorIiCalculado) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorIpiCalculado) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorIssRetido) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorIrrfRetido) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorInssRetido) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorPisRetido) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorCofinsRetido) || 0), 0) +
    itens.reduce((s, i) => s + (Number(i.valorCsllRetida) || 0), 0)
  // Usa o ValorLiquidoAPagarSnapshot se existir (valor que gerou o título do fornecedor),
  // senão calcula como fallback
  const primeiroItem = itens[0]
  const valorLiquidoSnapshot = primeiroItem?.valorLiquidoAPagarSnapshot
    ? itens.reduce((s, i) => s + (Number(i.valorLiquidoAPagarSnapshot) || 0), 0)
    : null
  const valorDoc = Number(formData.value?.valorTotalDocumento) || 0
  return {
    valorIcms: valorIcmsExibicao,
    valorIcmsEfetivo: valorIcmsExibicao,
    valorPis: itens.reduce((s, i) => s + (Number(i.valorPisCalculado) || 0), 0),
    valorCofins: itens.reduce((s, i) => s + (Number(i.valorCofinsCalculado) || 0), 0),
    valorDifal: itens.reduce((s, i) => s + (Number(i.valorDifalCalculado) || 0), 0),
    valorIe: itens.reduce((s, i) => s + (Number(i.valorIeCalculado) || 0), 0),
    valorIi: itens.reduce((s, i) => s + (Number(i.valorIiCalculado) || 0), 0),
    valorIpi: itens.reduce((s, i) => s + (Number(i.valorIpiCalculado) || 0), 0),
    valorIss: itens.reduce((s, i) => s + (Number(i.valorIssRetido) || 0), 0),
    valorIrrf: itens.reduce((s, i) => s + (Number(i.valorIrrfRetido) || 0), 0),
    valorInss: itens.reduce((s, i) => s + (Number(i.valorInssRetido) || 0), 0),
    valorPisRetido: itens.reduce((s, i) => s + (Number(i.valorPisRetido) || 0), 0),
    valorCofinsRetida: itens.reduce((s, i) => s + (Number(i.valorCofinsRetido) || 0), 0),
    valorCsll: itens.reduce((s, i) => s + (Number(i.valorCsllRetida) || 0), 0),
    // Alíquotas (média ponderada)
    aliquotaIcmsEfetiva: (() => {
      const itensComAliquota = itens.filter(i => i.aliquotaIcmsEfetivaAplicada > 0)
      if (itensComAliquota.length === 0) return null
      const soma = itensComAliquota.reduce((s, i) => s + Number(i.aliquotaIcmsEfetivaAplicada), 0)
      return (soma / itensComAliquota.length).toFixed(2)
    })(),
    aliquotaPis: (() => {
      const itensComAliquota = itens.filter(i => i.aliquotaPisAplicada > 0)
      if (itensComAliquota.length === 0) return null
      const soma = itensComAliquota.reduce((s, i) => s + Number(i.aliquotaPisAplicada), 0)
      return (soma / itensComAliquota.length).toFixed(2)
    })(),
    aliquotaCofins: (() => {
      const itensComAliquota = itens.filter(i => i.aliquotaCofinsAplicada > 0)
      if (itensComAliquota.length === 0) return null
      const soma = itensComAliquota.reduce((s, i) => s + Number(i.aliquotaCofinsAplicada), 0)
      return (soma / itensComAliquota.length).toFixed(2)
    })(),
    aliquotaDifal: (() => {
      const itensComAliquota = itens.filter(i => i.aliquotaDifalAplicada > 0)
      if (itensComAliquota.length === 0) return null
      const soma = itensComAliquota.reduce((s, i) => s + Number(i.aliquotaDifalAplicada), 0)
      return (soma / itensComAliquota.length).toFixed(2)
    })(),
    aliquotaIpi: (() => {
      const itensComAliquota = itens.filter(i => i.aliquotaIpiAplicada > 0)
      if (itensComAliquota.length === 0) return null
      const soma = itensComAliquota.reduce((s, i) => s + Number(i.aliquotaIpiAplicada), 0)
      return (soma / itensComAliquota.length).toFixed(2)
    })(),
    // Totais
    valorTotalImpostos: totalImpostos,
    valorTotalComImpostos: valorDoc,
    valorLiquidoAPagar: valorLiquidoSnapshot !== null ? valorLiquidoSnapshot : valorDoc,
    fatorImpostosMedio: (() => {
      const fatores = itens.map(i => Number(i.fatorImpostosAplicado)).filter(f => f > 0)
      return fatores.length > 0 ? fatores.reduce((s, f) => s + f, 0) / fatores.length : 0
    })(),
    totalItens: itens.length,
    itensComFiscal: itens.filter(i => i.valorIcmsCalculado != null).length
  }
})

// ============================================================
// DADOS FISCAIS UNIFICADOS PARA EXIBIÇÃO (step Fiscal)
// Unifica a exibição entre documento aprovado (dados persistidos)
// e simulação, garantindo o mesmo layout para ambas as origens.
// ============================================================
const fiscalDisplayData = computed(() => {
  // Documento aprovado: usa dados persistidos dos itens
  if (formData.value?.lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado && fiscalDataPersistido.value) {
    const p = fiscalDataPersistido.value
    return {
      _origem: 'persistido',
      titulo: 'Impostos do Documento (Persistidos)',
      totalItens: p.totalItens || 0,
      itensComFiscal: p.itensComFiscal || 0,
      totalImpostos: p.valorTotalImpostos || 0,
      valorLiquido: p.valorLiquidoAPagar || 0,
      valorTotal: p.valorTotalComImpostos || 0,
      fatorImpostos: p.fatorImpostosMedio || 0,
      impostos: [
        { chave: 'icms', label: 'ICMS (Efetivo)', valor: p.valorIcms, aliquota: p.aliquotaIcmsEfetiva },
        { chave: 'pis', label: 'PIS', valor: p.valorPis, aliquota: p.aliquotaPis },
        { chave: 'cofins', label: 'COFINS', valor: p.valorCofins, aliquota: p.aliquotaCofins },
        { chave: 'difal', label: 'DIFAL', valor: p.valorDifal, aliquota: p.aliquotaDifal },
        { chave: 'ie', label: 'IE', valor: p.valorIe, aliquota: null },
        { chave: 'ii', label: 'II', valor: p.valorIi, aliquota: null },
        { chave: 'ipi', label: 'IPI', valor: p.valorIpi, aliquota: p.aliquotaIpi },
        { chave: 'iss', label: 'ISS Retido', valor: p.valorIss, aliquota: null },
        { chave: 'irrf', label: 'IRRF', valor: p.valorIrrf, aliquota: null },
        { chave: 'inss', label: 'INSS', valor: p.valorInss, aliquota: null },
        { chave: 'pisRetido', label: 'PIS Retido', valor: p.valorPisRetido, aliquota: null },
        { chave: 'cofinsRetido', label: 'COFINS Retida', valor: p.valorCofinsRetida, aliquota: null },
        { chave: 'csll', label: 'CSLL Retida', valor: p.valorCsll, aliquota: null }
      ].filter(i => i.valor)
    }
  }
  // Simulação: usa resultado agregado da simulação
  if (simulacaoDocumentoResultado.value) {
    const s = simulacaoDocumentoResultado.value
    const valorIcmsExibicao = Number(s.valorIcmsEfetivo) || 0
    const totalImpostos =
      valorIcmsExibicao +
      (Number(s.valorPis) || 0) +
      (Number(s.valorCofins) || 0) +
      (Number(s.valorDifal) || 0) +
      (Number(s.valorIe) || 0) +
      (Number(s.valorIi) || 0) +
      (Number(s.valorIpi) || 0) +
      (Number(s.valorIss) || 0) +
      (Number(s.valorIrrf) || 0) +
      (Number(s.valorInss) || 0) +
      (Number(s.valorPisRetido) || 0) +
      (Number(s.valorCofinsRetida) || 0) +
      (Number(s.valorCsll) || 0)
    return {
      _origem: 'simulacao',
      titulo: 'Prévia dos Impostos do Documento',
      totalItens: s.totalItens || 0,
      itensComFiscal: s.itensOk || 0,
      totalImpostos,
      valorLiquido: s.valorLiquidoAPagar || 0,
      valorTotal: s.valorTotalComImpostos || 0,
      fatorImpostos: s.fatorImpostosMedio || 0,
      impostos: [
        { chave: 'icms', label: 'ICMS (Efetivo)', valor: valorIcmsExibicao, aliquota: s.aliquotaIcmsEfetiva ? Number(s.aliquotaIcmsEfetiva).toFixed(2) : null },
        { chave: 'pis', label: 'PIS', valor: s.valorPis, aliquota: s.aliquotaPis ? Number(s.aliquotaPis).toFixed(2) : null },
        { chave: 'cofins', label: 'COFINS', valor: s.valorCofins, aliquota: s.aliquotaCofins ? Number(s.aliquotaCofins).toFixed(2) : null },
        { chave: 'difal', label: 'DIFAL', valor: s.valorDifal, aliquota: s.aliquotaDifal ? Number(s.aliquotaDifal).toFixed(2) : null },
        { chave: 'ie', label: 'IE', valor: s.valorIe, aliquota: null },
        { chave: 'ii', label: 'II', valor: s.valorIi, aliquota: null },
        { chave: 'ipi', label: 'IPI', valor: s.valorIpi, aliquota: s.aliquotaIpi ? Number(s.aliquotaIpi).toFixed(2) : null },
        { chave: 'iss', label: 'ISS Retido', valor: s.valorIss, aliquota: null },
        { chave: 'irrf', label: 'IRRF', valor: s.valorIrrf, aliquota: null },
        { chave: 'inss', label: 'INSS', valor: s.valorInss, aliquota: null },
        { chave: 'pisRetido', label: 'PIS Retido', valor: s.valorPisRetido, aliquota: null },
        { chave: 'cofinsRetido', label: 'COFINS Retida', valor: s.valorCofinsRetida, aliquota: null },
        { chave: 'csll', label: 'CSLL Retida', valor: s.valorCsll, aliquota: null }
      ].filter(i => i.valor)
    }
  }
  return null
})

// ============================================================
// SIMULAÇÃO FISCAL AGREGADA DO DOCUMENTO (step Fiscal)
// ============================================================
const simulacaoDocumentoLoading = ref(false)
const simulacaoDocumentoResultado = ref(null)

const handleOperacaoSelecionado = (operacao) => {
  if (!operacao) return
  operacaoSelecionada.value = operacao
  // Ajusta moeda conforme regime: internacional libera, nacional fixa BRL
  if (operacao.cfopExterior && operacao.cfopExterior.length > 0) {
    // Modo Importação/Exportação: libera moeda para escolha
    if (formData.value) formData.value.lstMoeda = formData.value.lstMoeda || ''
  } else {
    // Modo Nacional: fixa a moeda no CÓDIGO DA LISTA (MOE.BRL = "MOE1"), não na sigla "BRL"
    if (formData.value) formData.value.lstMoeda = MOE.BRL
  }
}

const carregarDadosOperacao = async (idOperacao) => {
  if (!idOperacao) {
    operacaoSelecionada.value = null
    return
  }
  try {
    const res = await api.get(`/Operacoes/${idOperacao}`)
    operacaoSelecionada.value = res.data
  } catch (e) {
    console.error('Erro ao carregar dados da operação:', e)
    operacaoSelecionada.value = null
  }
}

const cacheDadosFiscaisProdutos = ref({})

const enriquecerItensComDadosFiscais = async (itens) => {
  if (!itens || itens.length === 0) return {}
  const idsProdutos = [...new Set(itens.map(i => i.idProduto).filter(id => id > 0))]
  const mapa = {}
  for (const idProduto of idsProdutos) {
    if (cacheDadosFiscaisProdutos.value[idProduto]) {
      mapa[idProduto] = cacheDadosFiscaisProdutos.value[idProduto]
      continue
    }
    try {
      const res = await api.get(`/Produtos/${idProduto}`)
      const prod = res.data
      const dados = {
        codigoNcm: prod.codigoNcm || null,
        lstCodigoNbs: prod.lstCodigoNbs || null,
        lstCodigoLc116: prod.lstCodigoLc116 || null,
        lstNaturezaItem: prod.lstNaturezaItem || null
      }
      cacheDadosFiscaisProdutos.value[idProduto] = dados
      mapa[idProduto] = dados
    } catch (e) {
      console.warn(`[SimulacaoDocumento] Erro ao buscar dados fiscais do produto ${idProduto}:`, e)
      mapa[idProduto] = { codigoNcm: null, lstCodigoNbs: null, lstCodigoLc116: null, lstNaturezaItem: null }
    }
  }
  return mapa
}

const executarSimulacaoDocumento = async () => {
  // Se o documento já foi aprovado, usa dados persistidos — não chama simulação
  if (formData.value?.lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado) {
    simulacaoDocumentoResultado.value = null
    return
  }
  const itens = formData.value?.itens
  if (!itens || itens.length === 0 || !fiscalPreviewContext.value?.idOperacao || !fiscalPreviewContext.value?.lstUfDestino) {
    simulacaoDocumentoResultado.value = null
    return
  }
  simulacaoDocumentoLoading.value = true
  try {
    const params = fiscalPreviewContext.value

    // PASSO 1: Enriquecer itens com dados fiscais dos produtos (NCM, NBS, LC116)
    const mapaDadosFiscais = await enriquecerItensComDadosFiscais(itens)

    // ================================================================
    // [DEBUG SIMULAÇÃO MODAL] Log completo do contexto fiscal
    // ================================================================
    console.log('========== [DEBUG SIMULAÇÃO MODAL] ==========')
    console.log('[DEBUG MODAL] fiscalPreviewContext:', JSON.parse(JSON.stringify(params)))
    console.log('[DEBUG MODAL] nivelFiscal (authStore):', nivelFiscal.value)
    console.log('[DEBUG MODAL] NV_MODULO_FISCAL.Nenhum:', NV_MODULO_FISCAL.Nenhum)
    console.log('[DEBUG MODAL] lstNivelModuloFiscalOverride a enviar:', nivelFiscal.value !== NV_MODULO_FISCAL.Nenhum ? nivelFiscal.value : null)
    console.log('[DEBUG MODAL] Total itens para simular:', itens.length)
    console.log('[DEBUG MODAL] LstTipoMovimento a enviar:', params.lstTipoMovimento)
    console.log('==============================================')

    const resultados = await Promise.allSettled(
      itens.map(item => {
        const dadosFiscais = mapaDadosFiscais[item.idProduto] || {}
        const isServico = (dadosFiscais.lstNaturezaItem || item.lstNaturezaItem || '') === 'PROD_NATUREZA2'
        const payload = {
          idOperacao: params.idOperacao,
          lstUfOrigem: params.lstUfOrigem || 'GEO_UF28',
          lstUfDestino: params.lstUfDestino,
          lstTipoMovimento: params.lstTipoMovimento,
          isServico,
          codigoNcm: isServico ? null : (dadosFiscais.codigoNcm || item.codigoNcm || null),
          codigoNbs: isServico ? (dadosFiscais.lstCodigoNbs || item.lstCodigoNbs || null) : null,
          codigoLc116: isServico ? (dadosFiscais.lstCodigoLc116 || item.lstCodigoLc116 || null) : null,
          lstNaturezaItem: dadosFiscais.lstNaturezaItem || item.lstNaturezaItem || null,
          valorBase: Number(item.valorTotalLinha) || 0,
          tipoPessoaParceiro: params.tipoPessoaParceiro || null,
          regimeTributarioParceiro: params.regimeTributarioParceiro || null,
          isOrgaoGovernoParceiro: !!params.isOrgaoGovernoParceiro,
          codigoIbge: params.codigoIbge || null,
          lstNivelModuloFiscalOverride: nivelFiscal.value !== NV_MODULO_FISCAL.Nenhum ? nivelFiscal.value : null
        }
        // ================================================================
        // [DEBUG SIMULAÇÃO MODAL] Log do payload de cada item
        // ================================================================
        console.log(`[DEBUG MODAL] Item ${item.idProduto} (${item.nomeProduto || 'sem nome'}):`)
        console.log(`  → isServico: ${isServico}`)
        console.log(`  → codigoNcm: ${payload.codigoNcm}`)
        console.log(`  → codigoNbs: ${payload.codigoNbs}`)
        console.log(`  → codigoLc116: ${payload.codigoLc116}`)
        console.log(`  → lstNaturezaItem: ${payload.lstNaturezaItem}`)
        console.log(`  → valorBase: ${payload.valorBase}`)
        console.log(`  → lstUfOrigem: ${payload.lstUfOrigem}`)
        console.log(`  → lstUfDestino: ${payload.lstUfDestino}`)
        console.log(`  → lstTipoMovimento: ${payload.lstTipoMovimento}`)
        console.log(`  → tipoPessoaParceiro: ${payload.tipoPessoaParceiro}`)
        console.log(`  → regimeTributarioParceiro: ${payload.regimeTributarioParceiro}`)
        console.log(`  → lstNivelModuloFiscalOverride: ${payload.lstNivelModuloFiscalOverride}`)
        console.log('  → PAYLOAD COMPLETO:', JSON.parse(JSON.stringify(payload)))
        console.log('==============================================')
        return api.post('/Fiscal/simular', payload).then(r => ({ data: r.data, idProduto: item.idProduto }))
      })
    )
    // Agrega os resultados
    const agregado = {
      totalItens: itens.length,
      itensOk: 0,
      itensFalha: 0,
      valorIcms: 0,
      valorIcmsEfetivo: 0,
      valorPis: 0,
      valorCofins: 0,
      valorDifal: 0,
      valorIe: 0,
      valorIi: 0,
      valorIpi: 0,
      valorIss: 0,
      valorIrrf: 0,
      valorInss: 0,
      valorPisRetido: 0,
      valorCofinsRetida: 0,
      valorCsll: 0,
      valorTotalComImpostos: 0,
      valorLiquidoAPagar: 0,
      fatorImpostosMedio: 0,
      // Alíquotas médias ponderadas
      aliquotaIcmsEfetiva: null,
      aliquotaPis: null,
      aliquotaCofins: null,
      aliquotaDifal: null,
      aliquotaIpi: null,
      detalhes: []
    }
    let somaFator = 0
    let qtdFator = 0
    let somaBaseIcms = 0
    let somaBasePis = 0
    let somaBaseCofins = 0
    let somaBaseDifal = 0
    let somaBaseIpi = 0
    let somaValorIcms = 0
    let somaValorPis = 0
    let somaValorCofins = 0
    let somaValorDifal = 0
    let somaValorIpi = 0
    for (const result of resultados) {
      if (result.status === 'fulfilled' && result.value?.data?.sucesso) {
        agregado.itensOk++
        const r = result.value.data
        agregado.valorIcms += Number(r.valorIcms) || 0
        agregado.valorIcmsEfetivo += Number(r.valorIcmsEfetivo) || 0
        agregado.valorPis += Number(r.valorPis) || 0
        agregado.valorCofins += Number(r.valorCofins) || 0
        agregado.valorDifal += Number(r.valorDifal) || 0
        agregado.valorIe += Number(r.valorIe) || 0
        agregado.valorIi += Number(r.valorIi) || 0
        agregado.valorIpi += Number(r.valorIpi) || 0
        agregado.valorIss += Number(r.valorIss) || 0
        agregado.valorIrrf += Number(r.valorIrrf) || 0
        agregado.valorInss += Number(r.valorInss) || 0
        agregado.valorPisRetido += Number(r.valorPisRetido) || 0
        agregado.valorCofinsRetida += Number(r.valorCofinsRetida) || 0
        agregado.valorCsll += Number(r.valorCsll) || 0
        agregado.valorTotalComImpostos += Number(r.valorTotalComImpostos) || 0
        agregado.valorLiquidoAPagar += Number(r.valorLiquidoAPagar) || 0
        if (Number(r.fatorImpostosAplicado) > 0) {
          somaFator += Number(r.fatorImpostosAplicado)
          qtdFator++
        }
        // Acumula para alíquotas médias ponderadas
        const baseIcms = Number(r.valorBaseIcms) || Number(r.valorIcms) || 0
        const basePis = Number(r.valorBasePis) || Number(r.valorPis) || 0
        const baseCofins = Number(r.valorBaseCofins) || Number(r.valorCofins) || 0
        const baseDifal = Number(r.valorBaseDifal) || Number(r.valorDifal) || 0
        const baseIpi = Number(r.valorBaseIpi) || Number(r.valorIpi) || 0
        if (baseIcms > 0 && Number(r.aliquotaIcmsEfetiva) > 0) {
          somaBaseIcms += baseIcms
          somaValorIcms += baseIcms * Number(r.aliquotaIcmsEfetiva)
        }
        if (basePis > 0 && Number(r.aliquotaPis) > 0) {
          somaBasePis += basePis
          somaValorPis += basePis * Number(r.aliquotaPis)
        }
        if (baseCofins > 0 && Number(r.aliquotaCofins) > 0) {
          somaBaseCofins += baseCofins
          somaValorCofins += baseCofins * Number(r.aliquotaCofins)
        }
        if (baseDifal > 0 && Number(r.aliquotaDifal) > 0) {
          somaBaseDifal += baseDifal
          somaValorDifal += baseDifal * Number(r.aliquotaDifal)
        }
        if (baseIpi > 0 && Number(r.aliquotaIpi) > 0) {
          somaBaseIpi += baseIpi
          somaValorIpi += baseIpi * Number(r.aliquotaIpi)
        }
        agregado.detalhes.push({ idProduto: result.value.idProduto, sucesso: true, perfil: r.nomePerfilAplicado })
      } else {
        agregado.itensFalha++
        const idProd = result.value?.idProduto || null
        agregado.detalhes.push({ idProduto: idProd, sucesso: false })
      }
    }
    agregado.fatorImpostosMedio = qtdFator > 0 ? (somaFator / qtdFator) : 0
    // Calcula alíquotas médias ponderadas
    agregado.aliquotaIcmsEfetiva = somaBaseIcms > 0 ? (somaValorIcms / somaBaseIcms) : null
    agregado.aliquotaPis = somaBasePis > 0 ? (somaValorPis / somaBasePis) : null
    agregado.aliquotaCofins = somaBaseCofins > 0 ? (somaValorCofins / somaBaseCofins) : null
    agregado.aliquotaDifal = somaBaseDifal > 0 ? (somaValorDifal / somaBaseDifal) : null
    agregado.aliquotaIpi = somaBaseIpi > 0 ? (somaValorIpi / somaBaseIpi) : null
    simulacaoDocumentoResultado.value = agregado
  } catch (e) {
    console.error('[SimulacaoDocumento] Erro ao simular impostos agregados:', e)
    simulacaoDocumentoResultado.value = null
  } finally {
    simulacaoDocumentoLoading.value = false
  }
}

// ============================================================
// TÍTULO DINÂMICO DO MODAL
// ============================================================
const tituloDinamico = computed(() => {
  if (!formData.value) return 'Novo Documento de Entrada'

  if (isVisualizacao.value) return 'Visualizar Documento de Entrada'

  const base = formData.value.idDocumentoEntrada ? 'Editar' : 'Novo'

  const apelido = formData.value.nomeFornecedor || ''
  const tipoDoc = formData.value.tipoDocumentoEntradaNome || ''
  const numDoc = formData.value.numeroDocumento || ''

  const sufixo = [apelido, tipoDoc, numDoc].filter(Boolean).join(', ')

  return sufixo ? `${base} — ${sufixo}` : `${base} Documento de Entrada`
})

// ============================================================
// STEPPER STATE
// ============================================================
const steps = reactive([
  { key: 'identificacao', label: 'Identificação', icon: 'bi-file-earmark', completed: false },
  { key: 'fornecedor', label: 'Fornecedor', icon: 'bi-truck', completed: false },
  { key: 'dadosDocumento', label: 'Dados', icon: 'bi-currency-dollar', completed: false },
  { key: 'itens', label: 'Itens', icon: 'bi-box-seam', completed: false },
  { key: 'fiscal', label: 'Fiscal', icon: 'bi-receipt', completed: false },
  // Step Modo Pagamento: sempre presente no array; a acessibilidade é controlada
  // pelo isStepAccessible() conforme o status do documento e a existência de itens
  { key: 'modoPagamento', label: 'Modo Pagamento', icon: 'bi-credit-card', completed: false }
])

const currentStep = ref(0)

const isStepAccessible = (index) => {
  // Etapa fiscal (index 4): acessível quando há itens no documento,
  // ou quando o documento está aprovado, ou em modo visualização
  if (index === 4) {
    return (formData.value?.itens?.length > 0) ||
           formData.value?.lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado ||
           isVisualizacao.value
  }

  // Etapa Modo Pagamento (index 5):
  // - Documento aprovado: sempre acessível (para visualizar/gerenciar parcelas)
  // - Modo visualização: sempre acessível
  // - Demais casos: acessível quando houver itens no documento
  if (index === 5) {
    return formData.value?.lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado ||
           isVisualizacao.value ||
           (formData.value?.itens?.length > 0)
  }

  // Etapa 0 (Identificação): sempre acessível
  if (index === 0) return true

  // Demais etapas: exigem Filial + Tipo Documento preenchidos
  const temIdentificacaoBasica = !!formData.value?.idFilial && !!formData.value?.idTipoDocumentoEntrada

  // Em modo edição (documento existente), libera se tiver identificação básica
  if (formData.value?.idDocumentoEntrada && temIdentificacaoBasica) {
    return true
  }
  // Em modo visualização, todas são acessíveis
  if (isVisualizacao.value) return true

  switch (index) {
    case 1: return temIdentificacaoBasica
    case 2: return temIdentificacaoBasica && !!formData.value?.idParceiroNegocioEmissor
    case 3: return temIdentificacaoBasica && !!formData.value?.idDocumentoEntrada
    default: return false
  }
}

const stepAvancar = () => {
  if (!formData.value) return
  // Marca o step atual como concluído antes de avançar
  steps[currentStep.value].completed = true
  const nextStep = currentStep.value + 1
  if (nextStep < steps.length && isStepAccessible(nextStep)) {
    currentStep.value++
  }
}

const stepVoltar = () => {
  if (!formData.value) return
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// ============================================================
// ESTADO DE BLOQUEIO E MODO APROVAÇÃO
// ============================================================
const modoAprovacao = ref(false)
const camposPendentes = ref(null)

// ============================================================
// RESOLUÇÃO DO idGrupoEmpresa REAL DO PARCEIRO
// ============================================================
const idGrupoEmpresaResolvido = ref(null)
const isResolvingGrupo = ref(false)

const resolverIdGrupoEmpresa = async (idParceiro) => {
  if (!idParceiro) {
    idGrupoEmpresaResolvido.value = null
    return
  }
  if (temDocumentoEntradaCompleto.value) {
    idGrupoEmpresaResolvido.value = null
    return
  }
  isResolvingGrupo.value = true
  try {
    const res = await api.get(`/Parceiros/${idParceiro}`)
    idGrupoEmpresaResolvido.value = res.data?.idGrupoEmpresa || null
  } catch (e) {
    console.error('Erro ao resolver idGrupoEmpresa do parceiro:', e)
    idGrupoEmpresaResolvido.value = null
  } finally {
    isResolvingGrupo.value = false
  }
}

// ============================================================
// CONFIGS DE BUSCA DINÂMICAS
// ============================================================
const configTipoDocumento = computed(() =>
  criarConfigBuscaDocumentoEntradaTipo(props.isInterno)
)

const configBuscaFornecedor = computed(() =>
  criarConfigBuscaFornecedorDocumentoEntrada(
    temDocumentoEntradaCompleto.value,
    idGrupoEmpresaResolvido.value
  )
)

const configBuscaEnderecoFaturamento = computed(() => {
  const idParceiro = formData.value?.idParceiroNegocioEmissor
  if (!idParceiro) return null
  return criarConfigBuscaEnderecoParceiro(idParceiro)
})

const configBuscaEnderecoExpedicao = computed(() => {
  const idParceiro = formData.value?.idParceiroNegocioEmissor
  if (!idParceiro) return null
  return criarConfigBuscaEnderecoParceiro(idParceiro)
})

const configBuscaFilial = computed(() => ({
  ...filialSearch,
  titulo: 'Buscar Filial'
}))

const configBuscaLocalEstoque = computed(() => {
  return criarConfigBuscaLocalEstoque(
    props.idOrganizacao || authStore.organizacaoAtiva,
    formData.value?.idFilial,
    props.lstTiposLocalEstoque
  )
})

const configBuscaCondicaoPagamento = computed(() =>
  criarConfigBuscaCondicaoPagamento()
)

// ============================================================
// ESTADO DO FORMULÁRIO
// ============================================================
const formData = ref(null)
const showAuditLog = ref(false)
const showCambioCustoModal = ref(false)

const listasData = ref({})

const perfisTributarios = ref([])

// ============================================================
// PARCELAS CONFIGURÁVEIS
// ============================================================
const parcelas = ref([])
const parcelasLoading = ref(false)
const parcelasSalvando = ref(false)
const parcelasEditadas = ref(false)
const parcelasSalvandoIndices = ref(new Set())

// Flag que indica que os itens/valores foram alterados após a última geração de parcelas
const parcelasDesatualizadas = ref(false)

// Valor líquido de referência para validação da soma das parcelas.
// Documento aprovado: usa o valor líquido dos snapshots persistidos nos itens.
// Documento em digitação: usa o valor da simulação fiscal.
const valorLiquidoReferencia = computed(() => {
  if (formData.value?.lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado && fiscalDataPersistido.value) {
    return fiscalDataPersistido.value.valorLiquidoAPagar || 0
  }
  return simulacaoDocumentoResultado.value?.valorLiquidoAPagar || 0
})

// Soma total do valor das parcelas
const somaParcelas = computed(() => {
  return parcelas.value.reduce((s, p) => s + (Number(p.valorLiquidoParcelado) || 0), 0)
})

// As parcelas estão consistentes com o valor líquido da simulação?
const parcelasConsistentes = computed(() => {
  if (parcelas.value.length === 0) return false
  const total = valorLiquidoReferencia.value
  if (total <= 0) return false
  return Math.abs(somaParcelas.value - total) < 0.01
})

// Erros de validação por parcela (índice -> array de mensagens)
const parcelasErros = computed(() => {
  const erros = {}
  const hoje = new Date().toISOString().split('T')[0]
  const arr = parcelas.value || []
  for (let i = 0; i < arr.length; i++) {
    const p = arr[i]
    if (!p) continue
    const msgs = []
    const valor = Number(p.valorLiquidoParcelado) || 0
    if (valor <= 0) msgs.push('Valor deve ser maior que zero')
    if (!p.dataPrevista) msgs.push('Data prevista é obrigatória')
    if (p.dataPrevista && p.dataPrevista < hoje) msgs.push('Vencimento no passado')
    if (i > 0) {
      const dataAnterior = arr[i - 1]?.dataPrevista
      if (p.dataPrevista && dataAnterior && p.dataPrevista < dataAnterior) {
        msgs.push(`Data anterior à parcela ${i} (${dataAnterior})`)
      }
    }
    if (msgs.length > 0) erros[i] = msgs
  }
  return erros
})

// Parcelas têm erros bloqueantes (valor zero/negativo ou data anterior)
const parcelasTemErroBloqueante = computed(() => {
  for (const [, msgs] of Object.entries(parcelasErros.value)) {
    if (msgs.some(m => m !== 'Vencimento no passado')) return true
  }
  return false
})

// Aprovação pode prosseguir? (parcelas consistentes, sem erros bloqueantes e não desatualizadas)
const parcelasProntasParaAprovacao = computed(() => {
  return parcelasConsistentes.value && !parcelasTemErroBloqueante.value && !parcelasDesatualizadas.value
})

// Dados da condição de pagamento selecionada (para controle do campo DataVencimento)
const condicaoPagamentoSelecionada = ref(null)

// Computed: campo DataVencimento fica desabilitado quando a condição tem DiaFixoVencimento
const dataVencimentoDisabled = computed(() => {
  if (!podeEditar.value) return true
  if (condicaoPagamentoSelecionada.value?.diaFixoVencimento) return true
  return false
})

// Calcula a data do primeiro vencimento com base na condição de pagamento
const calcularPrimeiraDataVencimento = (condicao) => {
  if (!condicao || !condicao.diaFixoVencimento) return null
  const dataBase = formData.value?.dataEmissao || new Date().toISOString().split('T')[0]
  const dataBaseDate = new Date(dataBase + 'T12:00:00')
  let offsetDias
  if (condicao.entrada) {
    offsetDias = 0
  } else {
    offsetDias = condicao.diasIntervalo || 0
  }
  const primeiraData = new Date(dataBaseDate)
  primeiraData.setDate(primeiraData.getDate() + offsetDias)
  const diaFixo = condicao.diaFixoVencimento
  const ultimoDia = new Date(primeiraData.getFullYear(), primeiraData.getMonth() + 1, 0).getDate()
  const diaAjustado = Math.min(diaFixo, ultimoDia)
  primeiraData.setDate(diaAjustado)
  return primeiraData.toISOString().split('T')[0]
}

// Snapshot do estado persistido para detectar edições pendentes
let parcelasSnapshot = []

const detectarEdicoesPendentes = () => {
  if (parcelas.value.length !== parcelasSnapshot.length) {
    parcelasEditadas.value = true
    return
  }
  for (let i = 0; i < parcelas.value.length; i++) {
    const a = parcelas.value[i]
    const b = parcelasSnapshot[i]
    if (!b ||
        a.numeroParcela !== b.numeroParcela ||
        a.dataPrevista !== b.dataPrevista ||
        Number(a.valorLiquidoParcelado) !== Number(b.valorLiquidoParcelado)) {
      parcelasEditadas.value = true
      return
    }
  }
  parcelasEditadas.value = false
}

const atualizarSnapshot = () => {
  parcelasSnapshot = JSON.parse(JSON.stringify(parcelas.value))
  parcelasEditadas.value = false
}

// Watch profundo nas parcelas para detectar edições
watch(parcelas, () => {
  detectarEdicoesPendentes()
}, { deep: true })

const carregarParcelasDoBanco = async (idDocumentoEntrada) => {
  if (!idDocumentoEntrada) return
  try {
    const res = await api.get('/DocumentosEntradaParcelas', {
      params: { idDocumentoEntrada }
    })
    parcelas.value = (res.data || []).map(p => ({
      idDocumentoEntradaParcela: p.idDocumentoEntradaParcela,
      numeroParcela: p.numeroParcela,
      dataPrevista: p.dataPrevista,
      valorLiquidoParcelado: p.valorLiquidoParcelado,
      idCondicaoPagamento: p.idCondicaoPagamento || null,
      idPurchaseOrderCondicaoPg: p.idPurchaseOrderCondicaoPg || null,
      descricao: p.descricao || null
    }))
    atualizarSnapshot()
  } catch (e) {
    console.error('Erro ao carregar parcelas persistidas:', e)
  }
}

const handleSalvarParcelaIndividual = async (index) => {
  const parcela = parcelas.value[index]
  if (!parcela) return

  parcelasSalvandoIndices.value.add(index)
  try {
    const payload = {
      idDocumentoEntrada: formData.value.idDocumentoEntrada,
      numeroParcela: parcela.numeroParcela,
      dataPrevista: parcela.dataPrevista,
      valorLiquidoParcelado: Number(parcela.valorLiquidoParcelado) || 0,
      idCondicaoPagamento: parcela.idCondicaoPagamento || null,
      idPurchaseOrderCondicaoPg: parcela.idPurchaseOrderCondicaoPg || null,
      descricao: parcela.descricao || null
    }

    if (parcela.idDocumentoEntradaParcela) {
      await api.put(`/DocumentosEntradaParcelas/${parcela.idDocumentoEntradaParcela}`, payload)
    } else {
      const res = await api.post('/DocumentosEntradaParcelas', payload)
      parcela.idDocumentoEntradaParcela = res.data.idDocumentoEntradaParcela
    }
    emit('show-notification', { message: `Parcela ${parcela.numeroParcela} salva com sucesso!`, type: 'success' })
    atualizarSnapshot()
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao salvar parcela.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    parcelasSalvandoIndices.value.delete(index)
  }
}

const handleSalvarTodasParcelas = async () => {
  if (!formData.value?.idDocumentoEntrada) {
    emit('show-notification', { message: 'Salve o documento primeiro antes de persistir as parcelas.', type: 'warning' })
    return
  }
  if (parcelas.value.length === 0) return

  parcelasSalvando.value = true
  try {
    const res = await api.post('/DocumentosEntradaParcelas/salvar-todas', {
      idDocumentoEntrada: formData.value.idDocumentoEntrada,
      parcelas: parcelas.value.map(p => ({
        idDocumentoEntradaParcela: p.idDocumentoEntradaParcela || null,
        numeroParcela: p.numeroParcela,
        dataPrevista: p.dataPrevista,
        valorLiquidoParcelado: Number(p.valorLiquidoParcelado) || 0,
        idCondicaoPagamento: p.idCondicaoPagamento || null,
        idPurchaseOrderCondicaoPg: p.idPurchaseOrderCondicaoPg || null,
        descricao: p.descricao || null
      }))
    })
    // Atualiza os IDs das parcelas com os retornados
    parcelas.value = (res.data || []).map(p => ({
      idDocumentoEntradaParcela: p.idDocumentoEntradaParcela,
      numeroParcela: p.numeroParcela,
      dataPrevista: p.dataPrevista,
      valorLiquidoParcelado: p.valorLiquidoParcelado,
      idCondicaoPagamento: p.idCondicaoPagamento || null,
      idPurchaseOrderCondicaoPg: p.idPurchaseOrderCondicaoPg || null,
      descricao: p.descricao || null
    }))
    atualizarSnapshot()
    emit('show-notification', { message: 'Todas as parcelas foram salvas com sucesso!', type: 'success' })
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao salvar todas as parcelas.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    parcelasSalvando.value = false
  }
}

watch(() => props.modelValue, async (aberto) => {
  if (!aberto) {
    formData.value = null
    modoAprovacao.value = false
    camposPendentes.value = null
    currentStep.value = 0
    steps.forEach(s => { s.completed = false })
    parcelas.value = []
    parcelasDesatualizadas.value = false
    parcelasEditadas.value = false
    simulacaoDocumentoResultado.value = null
    condicaoPagamentoSelecionada.value = null
    return
  }

  // NOTA: OrdemCompra é incluída para carregar LstMoedaPadrao, LstMoedaGerencial,
  // LstFonteCambio e LstTaxaCambioStatus — listas de câmbio que ainda não estão
  // mapeadas para DocumentoEntrada no Dicionário de Metadados.
  // ⚠️ Pendência: solicitar ao humano que adicione os mappings no Dicionário
  // via endpoint POST /api/Setup/exportar-seed-dicionario-metadados ou
  // cadastro manual na tela Listas do Sistema > Dicionário Metadados.
  const result = await metadataStore.carregarListasParaTabelas(['DocumentoEntrada', 'Produto', 'OrdemCompra'])
  listasData.value = { ...props.listas, ...result }

  try {
    const resPerfis = await api.get('/PerfisTributarios', {
      params: { idEmpresa: authStore.empresaAtiva, pageSize: 200 }
    })
    perfisTributarios.value = resPerfis.data || []
  } catch (e) {
    console.error('Erro ao carregar Perfis Tributários:', e)
    perfisTributarios.value = []
  }

  if (props.approvalConfig) {
    modoAprovacao.value = true
    camposPendentes.value = props.approvalConfig.camposPendentes || null
    currentStep.value = 2
  }

  if (props.documento?.idDocumentoEntrada) {
    try {
      const res = await api.get(`/DocumentosEntrada/${props.documento.idDocumentoEntrada}`)
      formData.value = res.data
    } catch (e) {
      console.error('Erro ao buscar documento de entrada:', e)
      formData.value = { ...props.documento }
    }
    // Carrega as parcelas persistidas do banco
    await carregarParcelasDoBanco(props.documento.idDocumentoEntrada)
  } else if (props.documento) {
    formData.value = { ...props.documento }
  } else {
    formData.value = {
      idDocumentoEntrada: null,
      idOrganizacao: props.idOrganizacao,
      idFilial: props.idFilial || null,
      filialIdentificacao: '',
      idLocalEstoque: props.idLocalEstoquePadrao || null,
      idParceiroNegocioEmissor: props.idParceiroNegocioEmissor || null,
      idTipoDocumentoEntrada: null,
      tipoDocumentoEntradaNome: '',
      numeroDocumento: '',
      serieDocumento: null,
      dataEmissao: new Date().toISOString().split('T')[0],
      dataVencimento: null,
      dataFatoGerador: null,
      idCondicaoPagamento: props.idCondicaoPagamento || null,
      condicaoPagamentoDescricao: '',
      lstFormaPagamento: props.lstFormaPagamento || null,
      lstFormaPagamentoInternacional: props.lstFormaPagamentoInternacional || null,
      idPurchaseOrder: props.idPurchaseOrder,
      idOrdemCompra: props.idOrdemCompra,
      idEmbarque: props.idEmbarque,
      idOperacao: props.idOperacao || null,
      operacaoDescricao: '',
      isInterno: props.isInterno,
      lstMoeda: props.lstMoeda || '',
      lstTipoTaxaConversao: props.lstTipoTaxaConversao || null,
      idPerfilTributario: null,
      idParceiroEnderecoFaturamento: null,
      idParceiroEnderecoExpedicao: null,
      valorTotalOriginal: 0,
      valorTotalDesconto: 0,
      valorTotalDocumento: 0,
      lstStatusDocumento: SUP_ST_DOC_ENT.EmDigitao,
      isIntegradoFinanceiro: false,
      lstLocalDocumento: props.lstLocalDocumento,
      itens: []
    }
  }

  // Carrega os dados da operação (movimentaEstoque) para controlar o campo Local de Estoque
  await carregarDadosOperacao(formData.value?.idOperacao)

  // Carrega os dados da condição de pagamento para bloquear DataVencimento se tiver DiaFixo
  if (formData.value?.idCondicaoPagamento) {
    try {
      const resCond = await api.get(`/CondicoesPagamento/${formData.value.idCondicaoPagamento}`)
      condicaoPagamentoSelecionada.value = resCond.data
      if (resCond.data?.diaFixoVencimento) {
        const dataCalculada = calcularPrimeiraDataVencimento(resCond.data)
        if (dataCalculada) {
          formData.value.dataVencimento = dataCalculada
        }
      }
    } catch (e) {
      console.error('Erro ao carregar condição de pagamento:', e)
      condicaoPagamentoSelecionada.value = null
    }
  }

  // Dispara a simulação fiscal assim que o documento é carregado, para que o valor líquido
  // esteja disponível para o cálculo de parcelas na etapa Modo Pagamento
  if (formData.value?.itens?.length > 0 && formData.value?.lstStatusDocumento !== SUP_ST_DOC_ENT.Aprovado) {
    executarSimulacaoDocumento()
  }

  // Se o documento carregou com fornecedor mas sem nome ou endereços, busca os dados completos via API
  if (formData.value?.idParceiroNegocioEmissor && (!formData.value?.nomeFornecedor || !formData.value?.idParceiroEnderecoFaturamento)) {
    try {
      const resParceiro = await api.get(`/Parceiros/${formData.value.idParceiroNegocioEmissor}`)
      const p = resParceiro.data
      if (!formData.value.nomeFornecedor) {
        formData.value.nomeFornecedor = p?.nomeFantasia || p?.razaoSocial || ''
      }
      if (!formData.value.idParceiroEnderecoFaturamento) {
        formData.value.idParceiroEnderecoFaturamento = p?.idEnderecoFornecedorFaturamento || null
      }
      if (!formData.value.idParceiroEnderecoExpedicao) {
        formData.value.idParceiroEnderecoExpedicao = p?.idEnderecoFornecedorExpedicao || null
      }
    } catch (e) {
      console.error('Erro ao buscar dados do fornecedor:', e)
    }
  }

  await resolverIdGrupoEmpresa(props.idParceiroNegocioEmissor)

  // Carrega dados fiscais do fornecedor e filial para a prévia dos impostos
  if (formData.value?.idParceiroNegocioEmissor) {
    await carregarDadosFiscaisFornecedor(formData.value.idParceiroNegocioEmissor)
  }
  if (formData.value?.idFilial) {
    await carregarDadosFiscaisFilial(formData.value.idFilial)
  }

  // Valida todos os steps assim que o modal abre, marcando como completed
  // os steps que já estão preenchidos (ícone verde + check)
  validarStepsAoAbrir()

  nextTick(() => {
    const campoBusca = document.getElementById('idTipoDocumentoEntrada')
    if (campoBusca) {
      const input = campoBusca.querySelector('input') || campoBusca
      if (input && typeof input.focus === 'function') {
        input.focus()
      }
    }
  })
}, { immediate: true })

watch(() => currentStep.value, (step) => {
  if (step === 4 && formData.value?.itens?.length > 0) {
    executarSimulacaoDocumento()
  }
})
watch(() => formData.value?.itens?.length, () => {
  if (formData.value?.itens?.length > 0) {
    executarSimulacaoDocumento()
  }
  nextTick(() => validarStepsAoAbrir())
}, { deep: true })

// Quando a simulação é recalculada, as parcelas existentes ficam desatualizadas.
// Só marca como desatualizadas se a soma das parcelas divergir do novo líquido —
// isso evita o falso positivo ao reabrir o modal, cenário em que a simulação
// sempre recalcula e gera um novo objeto de resultado.
// Revalida os steps em seguida para refletir o estado fiscal/parcelas atualizado.
watch(simulacaoDocumentoResultado, (novoResultado) => {
  if (parcelas.value.length > 0 && novoResultado) {
    parcelasDesatualizadas.value = !parcelasConsistentes.value
  }
  nextTick(() => validarStepsAoAbrir())
})

watch(() => formData.value?.idOperacao, async (novoId) => {
  if (props.modelValue) {
    await carregarDadosOperacao(novoId)
    validarStepsAoAbrir()
  }
})

watch(() => props.idParceiroNegocioEmissor, async (novoId) => {
  if (props.modelValue) {
    await resolverIdGrupoEmpresa(novoId)
  }
})

// ============================================================
// COMPUTED / FUNCTIONS
// ============================================================
const campoBuscaListaDisabled = (campoNome) => {
  // Fora do modo aprovação: campos sempre desabilitados (preenchimento automático via cadastro)
  if (!modoAprovacao.value) return true
  // Em modo aprovação: habilitado apenas se for um campo pendente
  return !camposPendentes.value?.[campoNome]
}

watch(() => props.approvalConfig, (config) => {
  if (!props.modelValue) return
  if (config) {
    modoAprovacao.value = true
    camposPendentes.value = config.camposPendentes || null
    currentStep.value = 2
  }
})

watch(() => props.documento, (doc) => {
  if (!props.modelValue || !doc) return
  formData.value = {
    ...doc,
    idEmbarque: doc.idEmbarque ?? props.idEmbarque,
    idOrdemCompra: doc.idOrdemCompra ?? props.idOrdemCompra,
    idEmbarqueContainer: doc.idEmbarqueContainer ?? props.idEmbarqueContainer,
    idPurchaseOrder: doc.idPurchaseOrder ?? props.idPurchaseOrder
  }
})

// ============================================================
// HANDLERS
// ============================================================
const handleTipoDocumentoSelecionado = (tipo) => {
  if (!tipo || !formData.value) return
  formData.value.idTipoDocumentoEntrada = tipo.idTipoDocumentoEntrada
  formData.value.tipoDocumentoEntradaNome = tipo.nome
  formData.value.isInterno = tipo.isDocumentoInterno || props.isInterno
  if (tipo.isDocumentoInterno) {
    formData.value.numeroDocumento = ''
    formData.value.dataEmissao = new Date().toISOString().split('T')[0]
  }
}

// ============================================================
// CONFIRMATION MODAL PARA TROCA DE FORNECEDOR COM ITENS
// ============================================================
const confirmacaoTrocaFornecedorRef = ref(null)
const fornecedorPendente = ref(null)
const fornecedorAnteriorId = ref(null)

const handleFornecedorSelecionado = async (fornecedor) => {
  if (!fornecedor || !formData.value) return

  const temItens = formData.value.itens && formData.value.itens.length > 0
  const fornecedorAtual = formData.value.idParceiroNegocioEmissor

  if (temItens && fornecedorAtual && fornecedorAtual !== fornecedor.idParceiro) {
    fornecedorPendente.value = fornecedor
    fornecedorAnteriorId.value = fornecedorAtual
    formData.value.idParceiroNegocioEmissor = fornecedorAtual
    if (confirmacaoTrocaFornecedorRef.value) {
      confirmacaoTrocaFornecedorRef.value.show()
    }
    return
  }

  await aplicarFornecedor(fornecedor)
}

const aplicarFornecedor = async (fornecedor) => {
  formData.value.idParceiroNegocioEmissor = fornecedor.idParceiro
  formData.value.nomeFornecedor = fornecedor.nomeFantasia || fornecedor.razaoSocial || ''
  formData.value.idParceiroEnderecoFaturamento = null
  formData.value.idParceiroEnderecoExpedicao = null
  await nextTick()
  formData.value.idParceiroEnderecoFaturamento = fornecedor.idEnderecoFornecedorFaturamento || null
  formData.value.idParceiroEnderecoExpedicao = fornecedor.idEnderecoFornecedorExpedicao || null
  await carregarDadosFiscaisFornecedor(fornecedor.idParceiro)
}

const carregarDadosFiscaisFornecedor = async (idParceiro) => {
  if (!idParceiro) {
    fornecedorData.value = null
    return
  }
  try {
    const res = await api.get(`/Parceiros/${idParceiro}`)
    const p = res.data
    // Resolve UF do endereço de faturamento
    let ufOrigem = null
    const idEndFat = p.idEnderecoFornecedorFaturamento
    if (idEndFat && p.enderecos?.length) {
      const endFat = p.enderecos.find(e => e.idParceiroEndereco === idEndFat)
      ufOrigem = endFat?.uf || null
    }
    fornecedorData.value = {
      lstTipoPessoa: p.lstTipoPessoa || null,
      lstRegimeTributario: p.lstRegimeTributario || null,
      isOrgaoGoverno: !!p.isOrgaoGoverno,
      ufOrigem: ufParaCodigo(ufOrigem)
    }
  } catch (e) {
    console.error('Erro ao carregar dados fiscais do fornecedor:', e)
    fornecedorData.value = null
  }
}

const carregarDadosFiscaisFilial = async (idFilial) => {
  if (!idFilial) {
    filialSelecionada.value = null
    return
  }
  try {
    const res = await api.get(`/Filiais/${idFilial}`)
    filialSelecionada.value = res.data
  } catch (e) {
    console.error('Erro ao carregar dados fiscais da filial:', e)
    filialSelecionada.value = null
  }
}

const handleConfirmacaoTrocaFornecedor = (confirmed) => {
  if (!fornecedorPendente.value) return

  if (confirmed) {
    formData.value.itens = []
    aplicarFornecedor(fornecedorPendente.value)
  }
  fornecedorPendente.value = null
  fornecedorAnteriorId.value = null
}

// ============================================================
// VALIDAÇÃO DO STATUS DA ORDEM DE COMPRA
// Regra de negócio: apenas Emitida e Faturada Parcialmente
// permitem gerar/editar Documentos de Entrada.
// ============================================================
const STATUS_OC_PERMITIDOS = [
  OC_STATUS.Emitida,
  OC_STATUS.FaturadaParcialmente
]

const statusOcPermiteDocumento = computed(() => {
  if (!props.lstStatusOrdemCompra) return true
  return STATUS_OC_PERMITIDOS.includes(props.lstStatusOrdemCompra)
})

const validarStatusOrdemCompra = () => {
  if (!props.lstStatusOrdemCompra || statusOcPermiteDocumento.value) return true
  const nomes = {
    [OC_STATUS.Rascunho]: 'Rascunho',
    [OC_STATUS.EmAprovacao]: 'Em Aprovação',
    [OC_STATUS.Aprovada]: 'Aprovada',
    [OC_STATUS.Emitida]: 'Emitida',
    [OC_STATUS.FaturadaParcialmente]: 'Faturada Parcialmente',
    [OC_STATUS.EncerradaParcialmente]: 'Encerrada Parcialmente',
    [OC_STATUS.Encerrada]: 'Encerrada',
    [OC_STATUS.Reprovada]: 'Reprovada',
    [OC_STATUS.Cancelada]: 'Cancelada'
  }
  const nomeStatus = nomes[props.lstStatusOrdemCompra] || props.lstStatusOrdemCompra
  emit('show-notification', {
    message: `O status da Ordem de Compra '${nomeStatus}' não permite vincular Documentos de Entrada. São permitidos apenas os status: Emitida ou Faturada Parcialmente.`,
    type: 'warning'
  })
  return false
}

const handleSalvar = async () => {
  if (isVisualizacao.value || !formData.value) return

  if (!validarCamposObrigatoriosFrontEnd()) {
    const primeiraMensagem = Object.values(formErrors.value)[0] || 'Preencha todos os campos obrigatórios.'
    emit('show-notification', { message: primeiraMensagem, type: 'warning' })
    focarPrimeiroErro()
    return
  }

  if (!validarStatusOrdemCompra()) return

  // Se houver parcelas editadas pendentes e o documento já existe, salva-as primeiro
  if (parcelasEditadas.value && formData.value.idDocumentoEntrada && parcelas.value.length > 0) {
    await handleSalvarTodasParcelas()
  }

  const payload = JSON.parse(JSON.stringify(formData.value))
  emit('salvar', payload)
}

const handleSalvarIntermediario = () => {
  if (isVisualizacao.value || !formData.value) return

  if (!validarCamposObrigatoriosFrontEnd()) {
    const primeiraMensagem = Object.values(formErrors.value)[0] || 'Preencha todos os campos obrigatórios.'
    emit('show-notification', { message: primeiraMensagem, type: 'warning' })
    focarPrimeiroErro()
    return
  }

  if (!validarStatusOrdemCompra()) return

  const payload = {
    ...JSON.parse(JSON.stringify(formData.value)),
    idEmbarque: formData.value.idEmbarque ?? props.idEmbarque,
    idOrdemCompra: formData.value.idOrdemCompra ?? props.idOrdemCompra,
    idEmbarqueContainer: formData.value.idEmbarqueContainer ?? props.idEmbarqueContainer,
    idPurchaseOrder: formData.value.idPurchaseOrder ?? props.idPurchaseOrder
  }
  emit('salvar-intermediario', payload)
}

const handleFechar = () => {
  if (modoAprovacao.value) {
    emit('cancelar-aprovacao')
  }
  emit('update:modelValue', false)
}

// ============================================================
// HANDLERS DE ITENS (alocar / atualizar / remover)
// ============================================================
const handleAlocarItem = async (dados) => {
  const idDoc = formData.value?.idDocumentoEntrada
  if (!idDoc) return

  const payload = {
    idDocumentoEntrada: idDoc,
    idProduto: dados.idProduto,
    idPurchaseOrderProduto: dados.idPurchaseOrderProduto || null,
    idOrdemCompraItem: dados.idOrdemCompraItem || null,
    idParceiroNegocioProdutor: dados.idParceiroNegocioProdutor || null,
    idLoteFabricacao: dados.idLoteFabricacao || null,
    idLocalEstoque: dados.idLocalEstoque || null,
    codigoNcm: dados.codigoNcm || null,
    lstModalidadeRateio: dados.lstModalidadeRateio || null,
    lstPaisOrigem: dados.lstPaisOrigem || null,
    quantidadeUnBasica: dados.quantidadeUnBasica,
    quantidadeUnFornecedor: dados.quantidadeUnFornecedor,
    quantidadeVolumes: dados.quantidadeVolumes ?? null,
    lstUnidadeMedidaVolume: dados.lstUnidadeMedidaVolume || null,
    lstUnidadeMedidaBasica: dados.lstUnidadeMedidaBasica,
    lstUnidadeMedidaFornecedor: dados.lstUnidadeMedidaFornecedor,
    fatorConversao: dados.fatorConversao,
    precoUnitario: dados.precoUnitario,
    valorTotalLinha: dados.valorTotalLinha,
    // --- BREAKDOWN ORIGINAL × DESCONTO (Desconto Comercial Obtido) ---
    valorUnitarioOriginal: dados.valorUnitarioOriginal ?? 0,
    valorUnitarioDesconto: dados.valorUnitarioDesconto ?? 0,
    valorTotalOriginal: dados.valorTotalOriginal ?? 0,
    valorTotalDesconto: dados.valorTotalDesconto ?? 0
  }

  try {
    await api.post('/DocumentosEntradaItens', payload)
    emit('show-notification', { message: 'Item alocado no documento com sucesso!', type: 'success' })
    await recarregarDocumento(idDoc)
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao alocar item no documento.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

const handleAtualizarItem = async (dados) => {
  const idDoc = formData.value?.idDocumentoEntrada
  if (!dados.idDocumentoEntradaItem || !idDoc) return

  const payload = {
    idDocumentoEntrada: idDoc,
    idProduto: dados.idProduto,
    idPurchaseOrderProduto: dados.idPurchaseOrderProduto || null,
    idOrdemCompraItem: dados.idOrdemCompraItem || null,
    idParceiroNegocioProdutor: dados.idParceiroNegocioProdutor || null,
    idLoteFabricacao: dados.idLoteFabricacao || null,
    idLocalEstoque: dados.idLocalEstoque || null,
    codigoNcm: dados.codigoNcm || null,
    lstModalidadeRateio: dados.lstModalidadeRateio || null,
    lstPaisOrigem: dados.lstPaisOrigem || null,
    quantidadeUnBasica: dados.quantidadeUnBasica,
    quantidadeUnFornecedor: dados.quantidadeUnFornecedor,
    quantidadeVolumes: dados.quantidadeVolumes ?? null,
    lstUnidadeMedidaVolume: dados.lstUnidadeMedidaVolume || null,
    lstUnidadeMedidaBasica: dados.lstUnidadeMedidaBasica,
    lstUnidadeMedidaFornecedor: dados.lstUnidadeMedidaFornecedor,
    fatorConversao: dados.fatorConversao,
    precoUnitario: dados.precoUnitario,
    valorTotalLinha: dados.valorTotalLinha,
    // --- BREAKDOWN ORIGINAL × DESCONTO (Desconto Comercial Obtido) ---
    valorUnitarioOriginal: dados.valorUnitarioOriginal ?? 0,
    valorUnitarioDesconto: dados.valorUnitarioDesconto ?? 0,
    valorTotalOriginal: dados.valorTotalOriginal ?? 0,
    valorTotalDesconto: dados.valorTotalDesconto ?? 0
  }

  try {
    await api.put(`/DocumentosEntradaItens/${dados.idDocumentoEntradaItem}`, payload)
    emit('show-notification', { message: 'Item atualizado no documento com sucesso!', type: 'success' })
    await recarregarDocumento(idDoc)
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao atualizar item no documento.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

const handleRemoverItem = async ({ idDocumentoEntradaItem }) => {
  const idDoc = formData.value?.idDocumentoEntrada
  if (!idDocumentoEntradaItem || !idDoc) return

  try {
    await api.delete(`/DocumentosEntradaItens/${idDocumentoEntradaItem}`)
    emit('show-notification', { message: 'Item removido do documento.', type: 'success' })
    await recarregarDocumento(idDoc)
  } catch (e) {
    emit('show-notification', { message: 'Erro ao remover item.', type: 'error' })
  }
}

const recarregarDocumento = async (idDocumentoEntrada) => {
  if (!idDocumentoEntrada) return
  try {
    const res = await api.get(`/DocumentosEntrada/${idDocumentoEntrada}`)
    formData.value = res.data
  } catch (e) {
    console.error('Erro ao recarregar documento após alteração de itens:', e)
  }
}

// ============================================================
// USEDOCUMENTOENTRADAACOES
// ============================================================
const confirmationModalRef = ref(null)

const {
  confirmarAcao,
  onConfirmarAcao,
  isLoadingAcao,
  approvalModalState,
  modalConfig
} = useDocumentoEntradaAcoes({
  onSuccess: (tipoAcao) => {
    emit('show-notification', {
      message: `Documento ${tipoAcao === 'aprovar' ? 'aprovado' : tipoAcao === 'excluir' ? 'excluído' : 'cancelado'} com sucesso.`,
      type: 'success'
    })
    emit('update:modelValue', false)
  },
  showNotification: (payload) => emit('show-notification', payload)
})

const podeExcluir = computed(() => {
  if (isVisualizacao.value || !formData.value?.idDocumentoEntrada) return false
  if (!podeEditar.value) return false
  return formData.value.lstStatusDocumento === SUP_ST_DOC_ENT.EmDigitao
})

const podeAprovar = computed(() => {
  if (isVisualizacao.value || !formData.value?.idDocumentoEntrada) return false
  if (!podeEditar.value) return false
  if (formData.value.lstStatusDocumento !== SUP_ST_DOC_ENT.EmDigitao) return false
  return true
})

watch(approvalModalState, (state) => {
  if (state) {
    modoAprovacao.value = true
    camposPendentes.value = state.camposPendentes
    formData.value = state.documento
    currentStep.value = 2
    nextTick(() => validarStepsAoAbrir())
  }
})

// Handler quando uma condição de pagamento é selecionada
const handleCondicaoPagamentoSelecionado = (condicao) => {
  if (!condicao || !formData.value) return
  condicaoPagamentoSelecionada.value = condicao
  if (condicao.diaFixoVencimento) {
    const dataCalculada = calcularPrimeiraDataVencimento(condicao)
    if (dataCalculada) {
      formData.value.dataVencimento = dataCalculada
    }
  } else {
    formData.value.dataVencimento = null
  }
}

// Recalcula a data de vencimento quando a data de emissão muda e há condição com dia fixo
watch(() => formData.value?.dataEmissao, () => {
  if (!formData.value) return
  const condicao = condicaoPagamentoSelecionada.value
  if (condicao?.diaFixoVencimento) {
    const dataCalculada = calcularPrimeiraDataVencimento(condicao)
    if (dataCalculada) {
      formData.value.dataVencimento = dataCalculada
    }
  }
})

const handleGerarParcelas = async () => {
  // Operação internacional (COMEX): condição não é exigida (cálculo via PurchaseOrder)
  if (!isExportacao.value && !formData.value?.idCondicaoPagamento) {
    emit('show-notification', { message: 'Selecione uma Condição de Pagamento primeiro.', type: 'warning' })
    return
  }

  // O valor base DEVE ser o valor líquido a pagar da simulação fiscal — NUNCA o valor total do documento
  let valorLiquido = simulacaoDocumentoResultado.value?.valorLiquidoAPagar

  // Se a simulação ainda não foi executada, executa agora antes de gerar parcelas
  if (!valorLiquido || valorLiquido <= 0) {
    emit('show-notification', { message: 'Calculando impostos para obter o valor líquido...', type: 'info' })
    await executarSimulacaoDocumento()
    valorLiquido = simulacaoDocumentoResultado.value?.valorLiquidoAPagar
  }

  if (!valorLiquido || valorLiquido <= 0) {
    emit('show-notification', {
      message: 'Não foi possível calcular o valor líquido a pagar. Verifique se há itens no documento e se os dados fiscais estão completos.',
      type: 'warning'
    })
    return
  }

  // Operação internacional (COMEX): exige documento salvo para o cálculo via PurchaseOrder
  if (isExportacao.value && !formData.value.idDocumentoEntrada) {
    emit('show-notification', { message: 'Salve o documento antes de gerar as parcelas internacionais.', type: 'warning' })
    return
  }

  parcelasLoading.value = true
  try {
    const payloadCalcParcelas = isExportacao.value
      ? {
          valorBase: valorLiquido,
          idDocumentoEntrada: formData.value.idDocumentoEntrada
        }
      : {
          idCondicaoPagamento: formData.value.idCondicaoPagamento,
          valorBase: valorLiquido,
          dataBaseEmissao: formData.value.dataVencimento || formData.value.dataEmissao || new Date().toISOString().split('T')[0]
        }
    const res = await api.post('/DocumentosEntrada/calcular-parcelas', payloadCalcParcelas)
    const novasParcelas = (res.data || []).map(p => ({
      numeroParcela: p.numeroParcela,
      dataPrevista: p.dataVencimento,
      valorLiquidoParcelado: p.valorFatia,
      idCondicaoPagamento: p.idCondicaoPagamento || null,
      idPurchaseOrderCondicaoPg: p.idPurchaseOrderCondicaoPg || null,
      descricao: p.descricao || null
    }))
    parcelas.value = novasParcelas

    // Se o documento já foi criado, persiste automaticamente no banco
    if (formData.value?.idDocumentoEntrada) {
      try {
        const resPersist = await api.post('/DocumentosEntradaParcelas/salvar-todas', {
          idDocumentoEntrada: formData.value.idDocumentoEntrada,
          parcelas: novasParcelas.map(p => ({
            idDocumentoEntradaParcela: null,
            numeroParcela: p.numeroParcela,
            dataPrevista: p.dataPrevista,
            valorLiquidoParcelado: Number(p.valorLiquidoParcelado) || 0,
            idCondicaoPagamento: p.idCondicaoPagamento || null,
            idPurchaseOrderCondicaoPg: p.idPurchaseOrderCondicaoPg || null,
            descricao: p.descricao || null
          }))
        })
        // Atualiza com os IDs retornados do banco
        parcelas.value = (resPersist.data || []).map(p => ({
          idDocumentoEntradaParcela: p.idDocumentoEntradaParcela,
          numeroParcela: p.numeroParcela,
          dataPrevista: p.dataPrevista,
          valorLiquidoParcelado: p.valorLiquidoParcelado,
          idCondicaoPagamento: p.idCondicaoPagamento || null,
          idPurchaseOrderCondicaoPg: p.idPurchaseOrderCondicaoPg || null,
          descricao: p.descricao || null
        }))
        emit('show-notification', { message: 'Parcelas geradas e salvas com sucesso!', type: 'success' })
      } catch (ePersist) {
        console.error('Erro ao persistir parcelas:', ePersist)
        emit('show-notification', { message: 'Parcelas calculadas, mas houve erro ao salvar no banco.', type: 'warning' })
      }
    }
    parcelasDesatualizadas.value = false
    atualizarSnapshot()
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao calcular parcelas.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    parcelasLoading.value = false
  }
}

const handleAdicionarParcela = () => {
  const ultima = parcelas.value.length > 0 ? parcelas.value[parcelas.value.length - 1] : null
  const novoNumero = (ultima?.numeroParcela || 0) + 1
  const ultimaData = ultima?.dataPrevista || formData.value?.dataEmissao || new Date().toISOString().split('T')[0]
  const novaData = new Date(ultimaData)
  novaData.setDate(novaData.getDate() + 30)
  const dataStr = novaData.toISOString().split('T')[0]

  parcelas.value.push({
    numeroParcela: novoNumero,
    dataPrevista: dataStr,
    valorLiquidoParcelado: 0
  })
}

const handleRemoverParcela = async (index) => {
  const parcela = parcelas.value[index]
  if (!parcela) return

  // Se a parcela já está persistida no banco, exclui também do backend
  if (parcela.idDocumentoEntradaParcela) {
    try {
      await api.delete(`/DocumentosEntradaParcelas/${parcela.idDocumentoEntradaParcela}`)
    } catch (e) {
      const errorMsg = e.response?.data?.message || 'Erro ao excluir parcela no banco.'
      emit('show-notification', { message: errorMsg, type: 'error' })
      return
    }
  }

  parcelas.value.splice(index, 1)
  // Renumera as parcelas
  parcelas.value.forEach((p, i) => { p.numeroParcela = i + 1 })
}

const formatarData = (data) => {
  if (!data) return ''
  const [ano, mes, dia] = data.split('-')
  return `${dia}/${mes}/${ano}`
}

const ordenarParcelasPorData = () => {
  parcelas.value.sort((a, b) => {
    if (a.dataPrevista < b.dataPrevista) return -1
    if (a.dataPrevista > b.dataPrevista) return 1
    return a.numeroParcela - b.numeroParcela
  })
  parcelas.value.forEach((p, i) => { p.numeroParcela = i + 1 })
}

// ============================================================
// UTILITÁRIOS
// ============================================================
const formatarDecimal = (valor) => {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0)
}

// ============================================================
// HELPERS PARA EXIBIÇÃO DO MODAL DE CÂMBIO E CUSTOS
// ============================================================
const getTextoLista = (lista, codigo) => {
  if (!lista || !codigo) return codigo || '-'
  const item = lista.find(m => String(m.idListaClienteItem) === String(codigo))
  return item ? item.textoLista : codigo
}

const obterSiglaMoeda = (codigoMoeda) => {
  if (!codigoMoeda) return '-'
  const idStr = String(codigoMoeda)
  const itemLista = (listasData.value.LstMoeda || []).find(m =>
    String(m.idListaClienteItem) === idStr
  )
  const texto = itemLista ? itemLista.textoLista : codigoMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : codigoMoeda
}

const labelTaxaPadrao = computed(() => {
  const siglaDoc = obterSiglaMoeda(formData.value?.lstMoeda)
  const siglaPadrao = obterSiglaMoeda(formData.value?.lstMoedaPadrao)
  return `Taxa ${siglaDoc}/${siglaPadrao}`
})

const labelTaxaGerencialPadrao = computed(() => {
  const siglaGerencial = obterSiglaMoeda(formData.value?.lstMoedaGerencial)
  const siglaPadrao = obterSiglaMoeda(formData.value?.lstMoedaPadrao)
  return `Taxa ${siglaGerencial}/${siglaPadrao}`
})

const labelTaxaCambioGerencial = computed(() => {
  const siglaDoc = obterSiglaMoeda(formData.value?.lstMoeda)
  const siglaGerencial = obterSiglaMoeda(formData.value?.lstMoedaGerencial)
  return `Taxa ${siglaDoc}/${siglaGerencial}`
})

const formatarDataBR = (data) => {
  if (!data) return '-'
  const [ano, mes, dia] = data.split('-')
  return `${dia}/${mes}/${ano}`
}

const formatarValor = (valor, casas = 2) => {
  if (valor === null || valor === undefined) return '-'
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }).format(valor)
}

const isAprovado = computed(() => {
  return formData.value?.lstStatusDocumento === SUP_ST_DOC_ENT.Aprovado
})

</script>

<template>
  <ModalFormularioV2
    v-model="modelValueComputed"
    :loading="loading"
    cor-header="var(--bs-info)"
    @confirmar="handleSalvar"
    @update:model-value="handleFechar"
    :tamanho="currentStep === 3 ? 'xl' : 'lg'"
  >
    <template #header>
      <div class="d-flex align-items-center gap-2">
        <i class="bi bi-file-earmark-text fs-5"></i>
        <span>{{ tituloDinamico }}</span>
      </div>
    </template>
    <div v-if="formData" class="documento-entrada-modal">
      <!-- STEPPER HORIZONTAL (wrapper full-width com borda) -->
      <div class="stepper-wrapper">
        <div class="stepper-horizontal" :class="{ 'stepper-constrained': currentStep === 3 }">
          <div v-for="(step, index) in steps" :key="step.key"
               class="step-item"
               :class="{
                 'step-active': currentStep === index,
                 'step-completed': step.completed,
                 'step-disabled': !step.completed && currentStep !== index && !isStepAccessible(index),
                 'step-clickable': isStepAccessible(index)
               }"
               @click="isStepAccessible(index) && (currentStep = index)">
            <div class="step-indicator">
              <i v-if="step.completed" class="bi bi-check-circle-fill"></i>
              <span v-else class="step-number">{{ index + 1 }}</span>
            </div>
            <span class="step-label">{{ step.label }}</span>
            <i class="step-icon" :class="step.icon"></i>
          </div>
        </div>
      </div>

      <!-- ETAPA 0: Identificação -->
      <div v-if="currentStep === 0" class="step-content">
        <div class="row g-3">
          <!-- Linha 1: Filial + Tipo de Documento -->
          <div class="col-md-6">
            <CampoBusca
              id="idFilial"
              label="Filial"
              v-model="formData.idFilial"
              :configuracao-busca="configBuscaFilial"
              :disabled="!podeEditar"
              required
            />
          </div>
          <div class="col-md-6">
            <CampoBusca
              id="idTipoDocumentoEntrada"
              label="Tipo de Documento"
              v-model="formData.idTipoDocumentoEntrada"
              :configuracaoBusca="configTipoDocumento"
              :disabled="!podeEditar"
              @selecionado="handleTipoDocumentoSelecionado"
              required
            />
          </div>
          <template v-if="formData.idTipoDocumentoEntrada">
            <!-- Linha 2: Operação (4) + Local Estoque (4) + Status (2) -->
            <div class="col-md-5">
              <CampoBusca
                id="idOperacao"
                label="Operação"
                v-model="formData.idOperacao"
                :configuracaoBusca="criarConfigBuscaOperacaoCompra()"
                :disabled="!podeEditar"
                @selecionado="handleOperacaoSelecionado"
                :required="nivelFiscal !== NV_MODULO_FISCAL.Essential"
              />
            </div>
            <div class="col-md-4">
              <CampoBusca
                id="idLocalEstoque"
                label="Local de Estoque"
                v-model="formData.idLocalEstoque"
                :configuracaoBusca="configBuscaLocalEstoque"
                :disabled="!podeEditar || !operacaoSelecionada?.movimentaEstoque"
                :required="operacaoSelecionada?.movimentaEstoque === true"
              />
              <small v-if="operacaoSelecionada && !operacaoSelecionada.movimentaEstoque" class="text-muted d-block mt-1">
                <i class="bi bi-info-circle me-1"></i>Operação não movimenta estoque
              </small>
            </div>
            <div class="col-md-3">
              <CampoSelect
                id="lstStatusDocumento"
                label="Status"
                v-model="formData.lstStatusDocumento"
                :options="listasData.LstStatusDocumento || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                disabled
                required
              />
            </div>
          </template>
        </div>
      </div>

      <!-- ETAPA 1: Fornecedor -->
      <div v-if="currentStep === 1" class="step-content">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoBusca
              id="idParceiroNegocioEmissor"
              label="Fornecedor Emissor"
              v-model="formData.idParceiroNegocioEmissor"
              :configuracaoBusca="configBuscaFornecedor"
              :disabled="!podeEditar"
              @selecionado="handleFornecedorSelecionado"
              required
            />
          </div>
          <div v-if="configBuscaEnderecoFaturamento" class="col-md-4">
            <CampoBusca
              id="idParceiroEnderecoFaturamento"
              label="Endereço de Faturamento"
              v-model="formData.idParceiroEnderecoFaturamento"
              :configuracaoBusca="configBuscaEnderecoFaturamento"
              :disabled="!podeEditar || !formData.idParceiroNegocioEmissor"
              required
            />
          </div>
          <div v-if="configBuscaEnderecoExpedicao" class="col-md-4">
            <CampoBusca
              id="idParceiroEnderecoExpedicao"
              label="Endereço de Expedição"
              v-model="formData.idParceiroEnderecoExpedicao"
              :configuracaoBusca="configBuscaEnderecoExpedicao"
              :disabled="!podeEditar || !formData.idParceiroNegocioEmissor"
              required
            />
          </div>
        </div>
      </div>

      <!-- ETAPA 2: Dados do Documento -->
      <div v-if="currentStep === 2" class="step-content">
        <div class="row g-3">
          <template v-if="formData.idTipoDocumentoEntrada">
            <div class="col-md-5">
              <CampoInput
                id="numeroDocumento"
                label="Nº Documento"
                v-model="formData.numeroDocumento"
                :disabled="formData.isInterno || !podeEditar"
                :readonly="formData.isInterno"
                :required="!formData.isInterno"
              />
            </div>
            <div class="col-md-7">
              <CampoInput
                id="serieDocumento"
                label="Nº Série"
                v-model="formData.serieDocumento"
                :disabled="!podeEditar"
                maxlength="20"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="dataEmissao"
                label="Data de Emissão"
                v-model="formData.dataEmissao"
                type="date"
                :disabled="formData.isInterno || !podeEditar"
                :readonly="formData.isInterno"
                :required="!formData.isInterno"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="dataFatoGerador"
                label="Data do Fato Gerador"
                v-model="formData.dataFatoGerador"
                type="date"
                :disabled="!podeEditar"
              />
            </div>
            <div class="col-md-2">
              <CampoSelect
                id="lstMoeda"
                label="Moeda"
                v-model="formData.lstMoeda"
                :options="listasData.LstMoeda || []"
                option-label="textoLista"
                option-value="idListaClienteItem"
                :disabled="!!props.lstMoeda || !podeEditar"
                required
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="valorTotalOriginal"
                label="Valor Original"
                :model-value="formData.valorTotalOriginal"
                type="moeda"
                disabled
                readonly
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="valorTotalDesconto"
                label="Valor Desconto"
                :model-value="formData.valorTotalDesconto"
                type="moeda"
                disabled
                readonly
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="valorTotalDocumento"
                label="Valor Total (Negociado)"
                :model-value="formData.valorTotalDocumento"
                type="moeda"
                disabled
                readonly
              />
            </div>
            <!-- Tipo Taxa Conversão -->
            <div class="col-md-6">
              <div class="campo-busca-lista-wrapper mb-3">
                <label for="lstTipoTaxaConversao" class="form-label"
                       :class="{ 'is-required': modoAprovacao && camposPendentes?.lstTipoTaxaConversao }">
                  Tipo Taxa Conversão
                  <i class="bi bi-info-circle ms-1 text-primary"
                     data-bs-toggle="tooltip"
                     title="Taxa utilizada para conversão para as moedas Padrão e Gerencial"
                     style="cursor: help;"></i>
                </label>
                <CampoBuscaLista
                  id="lstTipoTaxaConversao"
                  label=""
                  v-model="formData.lstTipoTaxaConversao"
                  tabela="DocumentoEntrada"
                  :disabled="campoBuscaListaDisabled('lstTipoTaxaConversao')"
                  hide-label
                  :required="modoAprovacao && camposPendentes?.lstTipoTaxaConversao"
                />
              </div>
            </div>
          </template>
          <div v-if="!formData.idTipoDocumentoEntrada" class="col-12">
            <div class="text-center py-3 text-muted">
              <i class="bi bi-info-circle me-2"></i>
              Selecione o Tipo de Documento para habilitar os campos.
            </div>
          </div>
        </div>
      </div>

      <!-- ETAPA 5: Modo Pagamento -->
      <div v-if="currentStep === 5" class="step-content">
        <div class="row g-3">
          <template v-if="formData.idTipoDocumentoEntrada">
            <div class="col-md-5">
              <CampoBusca
                id="idCondicaoPagamento"
                :label="isExportacao ? 'Condição de Pagamento (Nacional)' : 'Condição de Pagamento'"
                v-model="formData.idCondicaoPagamento"
                :configuracaoBusca="configBuscaCondicaoPagamento"
                :disabled="!podeEditar || isExportacao"
                @selecionado="handleCondicaoPagamentoSelecionado"
              />
              <small v-if="isExportacao" class="text-muted d-block mt-1">
                <i class="bi bi-info-circle me-1"></i>Condição herdada da PurchaseOrder — não editável em operação internacional.
              </small>
            </div>
            <div v-if="!isExportacao" class="col-md-3">
              <CampoInput
                id="dataVencimento"
                label="Data Referência Vencimento"
                v-model="formData.dataVencimento"
                type="date"
                :disabled="dataVencimentoDisabled"
              />
            </div>
            <div v-if="!isExportacao" class="col-md-4">
              <div class="campo-busca-lista-wrapper mb-3">
                <label for="lstFormaPagamento" class="form-label">Forma de Pagamento (Nacional)</label>
                <CampoBuscaLista
                  id="lstFormaPagamento"
                  label=""
                  v-model="formData.lstFormaPagamento"
                  tabela="DocumentoEntrada"
                  :disabled="!podeEditar"
                  hide-label
                />
              </div>
            </div>
            <div v-if="isExportacao" class="col-md-7">
              <div class="campo-busca-lista-wrapper mb-3">
                <label for="lstFormaPagamentoInternacional" class="form-label">Forma de Pagamento (Internacional)</label>
                <CampoBuscaLista
                  id="lstFormaPagamentoInternacional"
                  label=""
                  v-model="formData.lstFormaPagamentoInternacional"
                  tabela="DocumentoEntrada"
                  :disabled="!podeEditar"
                  hide-label
                />
              </div>
            </div>

            <!-- Grid de Parcelas Configuráveis -->
            <div class="col-12 mt-3">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="mb-0 fw-bold">
                  <i class="bi bi-layout-three-columns me-1"></i>Parcelas
                </h6>
                <div class="d-flex gap-2">
                  <!-- Botão Salvar Todas (só aparece quando há edição pendente e documento já existe) -->
                  <button
                    v-if="parcelas.length > 0 && podeEditar && formData?.idDocumentoEntrada"
                    type="button"
                    class="btn btn-sm btn-success"
                    :disabled="!parcelasEditadas || parcelasSalvando"
                    @click="handleSalvarTodasParcelas"
                  >
                    <i v-if="parcelasSalvando" class="bi bi-arrow-repeat spin me-1"></i>
                    <i v-else class="bi bi-save2 me-1"></i>
                    Salvar Todas
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-info"
                    :disabled="(!isExportacao && !formData.idCondicaoPagamento) || parcelasLoading || !podeEditar"
                    @click="handleGerarParcelas"
                  >
                    <i v-if="parcelasLoading" class="bi bi-arrow-repeat spin me-1"></i>
                    <i v-else class="bi bi-calculator me-1"></i>
                    Gerar Parcelas
                  </button>
                  <button
                    v-if="parcelas.length > 0 && podeEditar"
                    type="button"
                    class="btn btn-sm btn-outline-success"
                    @click="handleAdicionarParcela"
                  >
                    <i class="bi bi-plus-lg me-1"></i>Adicionar
                  </button>
                </div>
              </div>

              <!-- Alertas de validação -->
              <div v-if="parcelas.length > 0 && parcelasDesatualizadas" class="alert alert-warning py-2 mb-2" style="font-size:0.8rem;">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>Parcelas desatualizadas!</strong> Os valores dos itens ou impostos foram alterados.
                Clique em <strong>"Gerar Parcelas"</strong> para recalcular.
              </div>

              <div v-if="parcelas.length > 0 && !parcelasConsistentes && !parcelasDesatualizadas" class="alert alert-danger py-2 mb-2" style="font-size:0.8rem;">
                <i class="bi bi-x-circle-fill me-2"></i>
                <strong>Valor total das parcelas diferente do líquido a pagar!</strong>
                Soma: {{ formatarDecimal(somaParcelas) }} | Líquido: {{ formatarDecimal(valorLiquidoReferencia) }}
              </div>

              <!-- Grid -->
              <div v-if="parcelas.length > 0" class="table-responsive">
                <table class="table table-sm table-bordered align-middle mb-0 parcelas-grid" style="font-size:0.85rem;">
                  <thead>
                    <tr>
                      <th class="text-center" style="width:80px;">Parcela</th>
                      <th style="width:160px;">Data Prevista</th>
                      <th>Valor Líquido Parcelado</th>
                      <th v-if="podeEditar" class="text-center" style="width:100px;">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(parcela, index) in parcelas" :key="index"
                      :class="{
                        'table-danger': parcelasErros && parcelasErros[index]?.some(m => m !== 'Vencimento no passado'),
                        'table-warning': parcelasErros && parcelasErros[index]?.length > 0 && !(parcelasErros[index]?.some(m => m !== 'Vencimento no passado'))
                      }">
                      <td class="text-center fw-bold">
                        {{ parcela.numeroParcela }}
                        <i v-if="parcelasErros && parcelasErros[index]?.length" class="bi bi-exclamation-circle text-danger ms-1"
                           :title="parcelasErros[index]?.join('; ')"></i>
                      </td>
                      <td>
                        <div v-if="podeEditar">
                          <input
                            type="date"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': parcelasErros && parcelasErros[index]?.some(m => m.includes('anterior') || m.includes('obrigatória')) }"
                            v-model="parcela.dataPrevista"
                            @blur="ordenarParcelasPorData"
                          />
                          <small v-if="parcelasErros && parcelasErros[index]?.some(m => m.includes('passado'))" class="text-warning d-block" style="font-size:0.65rem;">
                            <i class="bi bi-exclamation-triangle me-1"></i>Vencimento no passado
                          </small>
                        </div>
                        <span v-else>{{ formatarData(parcela.dataPrevista) }}</span>
                      </td>
                      <td>
                        <div v-if="podeEditar">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            class="form-control form-control-sm text-end"
                            :class="{ 'is-invalid': parcelasErros && parcelasErros[index]?.some(m => m.includes('Valor')) }"
                            v-model="parcela.valorLiquidoParcelado"
                          />
                        </div>
                        <span v-else class="text-end d-block">{{ formatarDecimal(parcela.valorLiquidoParcelado) }}</span>
                      </td>
                      <td v-if="podeEditar" class="text-center">
                        <div class="d-flex gap-1 justify-content-center">
                          <!-- Botão Salvar Individual -->
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-primary border-0 p-1"
                            title="Salvar esta parcela"
                            :disabled="parcelasSalvandoIndices.has(index)"
                            @click="handleSalvarParcelaIndividual(index)"
                          >
                            <i v-if="parcelasSalvandoIndices.has(index)" class="bi bi-arrow-repeat spin"></i>
                            <i v-else class="bi bi-check2"></i>
                          </button>
                          <!-- Botão Remover -->
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-danger border-0 p-1"
                            title="Remover parcela"
                            @click="handleRemoverParcela(index)"
                          >
                            <i class="bi bi-trash3"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="parcelas-total fw-bold"
                      :class="{ 'table-success': parcelasConsistentes, 'table-danger': !parcelasConsistentes && parcelas.length > 0 }">
                      <td colspan="2" class="text-end">Total:</td>
                      <td class="text-end">
                        {{ formatarDecimal(somaParcelas) }}
                        <small v-if="valorLiquidoReferencia > 0" class="text-muted ms-1" style="font-size:0.65rem;">
                          / {{ formatarDecimal(valorLiquidoReferencia) }}
                        </small>
                        <i v-if="parcelasConsistentes" class="bi bi-check-circle-fill text-success ms-1"></i>
                        <i v-else-if="!parcelasDesatualizadas" class="bi bi-x-circle-fill text-danger ms-1"></i>
                      </td>
                      <td v-if="podeEditar"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div v-else class="text-center py-2 text-muted small">
                <i class="bi bi-info-circle me-1"></i>
                Selecione uma Condição de Pagamento e clique em "Gerar Parcelas" para visualizar as parcelas.
              </div>
            </div>
          </template>
          <div v-if="!formData.idTipoDocumentoEntrada" class="col-12">
            <div class="text-center py-3 text-muted">
              <i class="bi bi-info-circle me-2"></i>
              Selecione o Tipo de Documento para habilitar os campos.
            </div>
          </div>
        </div>
      </div>

      <!-- ETAPA 4: Fiscal -->
      <div v-if="currentStep === 4" class="step-content">
        <div class="row g-3">
          <template v-if="formData.idTipoDocumentoEntrada">
            <div v-if="nivelFiscal === NV_MODULO_FISCAL.Essential" class="col-md-6">
              <CampoSelect
                id="idPerfilTributario"
                label="Perfil Tributário"
                v-model="formData.idPerfilTributario"
                :options="perfisTributarios"
                option-label="descricao"
                option-value="idPerfilTributario"
                :required="nivelFiscal === NV_MODULO_FISCAL.Essential"
              />
            </div>
            <div v-if="nivelFiscal !== NV_MODULO_FISCAL.Essential" class="col-12">
              <!-- Loading -->
              <div v-if="simulacaoDocumentoLoading" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-info me-2"></div>
                Calculando impostos do documento...
              </div>

              <!-- Sem itens -->
              <div v-else-if="!formData.itens?.length" class="text-center py-3 text-muted">
                <i class="bi bi-inbox me-2"></i>
                Adicione itens ao documento para visualizar a prévia dos impostos.
              </div>

              <!-- Card fiscal unificado com alíquotas -->
              <FiscalPreviewCard
                v-else-if="fiscalDisplayData"
                :titulo="fiscalDisplayData.titulo"
                :origem="fiscalDisplayData._origem"
                :loading="false"
                :total-itens="fiscalDisplayData.totalItens"
                :itens-com-fiscal="fiscalDisplayData.itensComFiscal"
                :impostos="fiscalDisplayData.impostos"
                :total-impostos="fiscalDisplayData.totalImpostos"
                :valor-liquido="fiscalDisplayData.valorLiquido"
                :valor-total="fiscalDisplayData.valorTotal"
                :fator-impostos="fiscalDisplayData.fatorImpostos"
              />

              <!-- Nenhum resultado (fallback) -->
              <div v-else class="text-center py-3 text-muted">
                <i class="bi bi-robot me-2"></i>Impostos Calculados Automaticamente pelo Motor Fiscal
              </div>
            </div>
          </template>
          <div v-if="nivelFiscal === NV_MODULO_FISCAL.Compliance && !isCadastroValido" class="col-12">
            <div class="alert alert-danger mb-0 py-2">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              Preencha os dados fiscais obrigatórios antes de faturar.
            </div>
          </div>
        </div>
      </div>

      <!-- ETAPA 3: Itens (Smart Mode) -->
      <div v-if="currentStep === 3" class="step-content">
        <!-- Botão de salvamento intermediário -->
        <div v-if="exibirBotaoSalvarSmartMode" class="mb-3">
          <div class="alert alert-info d-flex align-items-center justify-content-between py-3 mb-0">
            <div>
              <i class="bi bi-info-circle me-2 fs-5"></i>
              <strong>Itens do Documento</strong>
              <span class="ms-2 text-muted">Salve o documento primeiro para liberar o gerenciamento de itens.</span>
            </div>
            <button
              type="button"
              class="btn btn-info text-white fw-bold"
              @click="handleSalvarIntermediario"
            >
              <i class="bi bi-save me-1"></i> Salvar e Liberar Itens
            </button>
          </div>
        </div>

        <div v-if="comSmartMode && formData?.idDocumentoEntrada">
          <div v-if="!formData.idParceiroNegocioEmissor" class="alert alert-warning d-flex align-items-center py-3 mb-0">
            <i class="bi bi-exclamation-triangle me-2 fs-5"></i>
            <div>
              <strong>Fornecedor não selecionado.</strong>
              <span class="ms-1 text-muted">Selecione um Fornecedor Emissor antes de gerenciar os itens do documento.</span>
            </div>
          </div>
          <DocumentoEntradaItensSmartMode
            v-else
            :key="'doc-' + formData.idDocumentoEntrada"
            :id-documento-entrada="formData.idDocumentoEntrada"
            :itens-documento="formData.itens || []"
            :listas="props.listas"
            :pode-editar="podeEditar"
            :habilitar-parceiro-produtor="props.habilitarParceiroProdutor"
            :config-busca-lote-fabricacao="props.configBuscaLoteFabricacao"
            :id-parceiro-negocio-fornecedor="formData?.idParceiroNegocioEmissor || props.idParceiroNegocioFornecedor"
            :lst-moeda-documento="formData.lstMoeda || ''"
            :nivel-fiscal="nivelFiscal"
            :lst-status-documento="formData.lstStatusDocumento"
            :modo-alocacao="modoAlocacaoSmartMode"
            :exibir-modalidade-rateio="exibirModalidadeRateio"
            :eh-operacao-nacional="ehOperacaoNacional"
            :contexto-origem="props.contextoOrigem || 'outros'"
            :fiscal-preview-params="fiscalPreviewContext"
            :id-local-estoque-padrao="formData.idLocalEstoque || props.idLocalEstoquePadrao"
            :movimenta-estoque="operacaoSelecionada?.movimentaEstoque || false"
            :config-busca-local-estoque="configBuscaLocalEstoque"
            @alocar-item="handleAlocarItem"
            @atualizar-item="handleAtualizarItem"
            @remover-item="handleRemoverItem"
            @show-notification="(p) => emit('show-notification', p)"
          />
        </div>
      </div>

      <!-- NAVEGAÇÃO DO STEPPER (sempre full-width) -->
      <div class="step-navigation">
        <button type="button" class="btn btn-outline-secondary"
                :disabled="currentStep === 0" @click="stepVoltar">
          <i class="bi bi-chevron-left me-1"></i> Voltar
        </button>
        <span class="step-counter">Passo {{ currentStep + 1 }} de {{ steps.length }}</span>
        <button v-if="currentStep < steps.length - 1" type="button" class="btn btn-info text-white"
                @click="stepAvancar">
          Avançar <i class="bi bi-chevron-right ms-1"></i>
        </button>
      </div>
    </div>

    <!-- FOOTER SLOT -->
    <template #footer>
      <div class="d-flex gap-2 w-100 justify-content-between">
        <div class="d-flex gap-2">
          <button v-if="podeExcluir" type="button" class="btn btn-outline-danger"
                  @click="confirmarAcao('excluir', formData, confirmationModalRef)">
            <i class="bi bi-trash me-1"></i> Excluir
          </button>
          <button v-if="isAprovado" type="button" class="btn btn-outline-info"
                  @click="showCambioCustoModal = true">
            <i class="bi bi-currency-exchange me-1"></i> Câmbio e Custos
          </button>
        </div>
        <div class="d-flex gap-2">
          <button
            v-if="formData?.idDocumentoEntrada"
            type="button"
            class="btn btn-sm border-0 p-1"
            title="Auditoria"
            @click="showAuditLog = true"
          >
            <i class="bi bi-clock-history fs-5"></i>
          </button>
          <button type="button" class="btn btn-outline-secondary" @click="handleFechar">
            Cancelar
          </button>
          <button v-if="podeAprovar" type="button" class="btn btn-success"
                  :disabled="isLoadingAcao" @click="confirmarAcao('aprovar', formData, confirmationModalRef)">
            <i class="bi bi-check-circle me-1"></i> Aprovar
          </button>
          <button v-if="exibirBotaoSalvarSmartMode && currentStep < 5" type="button"
                  class="btn btn-info text-white fw-bold" @click="handleSalvarIntermediario">
            <i class="bi bi-save me-1"></i> Salvar e Liberar Itens
          </button>
          <button v-if="!isVisualizacao" type="button" class="btn btn-info text-white fw-bold"
                  :disabled="loading" @click="handleSalvar">
            <i class="bi bi-save me-1"></i> Salvar
          </button>
        </div>
      </div>
    </template>

    <!-- ConfirmationModal para troca de fornecedor com itens lançados -->
    <Teleport to="body">
      <ConfirmationModal
        ref="confirmacaoTrocaFornecedorRef"
        title="Alterar Fornecedor?"
        message="Esta ação vai remover todos os itens lançados no documento. Deseja prosseguir com esta alteração?"
        confirm-button-text="Sim, remover itens e alterar"
        cancel-button-text="Não, cancelar alteração"
        @confirmed="handleConfirmacaoTrocaFornecedor"
      />
    </Teleport>

    <!-- ConfirmationModal para ações (excluir/cancelar/aprovar) -->
    <Teleport to="body">
      <ConfirmationModal
        ref="confirmationModalRef"
        :title="modalConfig.title"
        :message="modalConfig.message"
        :confirm-button-text="modalConfig.confirmButtonText"
        :cancel-button-text="modalConfig.cancelButtonText"
        @confirmed="onConfirmarAcao"
      />
    </Teleport>

    <!-- AuditLogViewer -->
    <Teleport to="body">
      <AuditLogViewer
        v-if="showAuditLog"
        tabela="DocumentoEntrada"
        :chave-primaria="formData.idDocumentoEntrada"
        @fechar="showAuditLog = false"
      />
    </Teleport>

    <!-- ================================================================ -->
    <!-- MODAL: Câmbio e Custos (apenas visível após aprovação)           -->
    <!-- ================================================================ -->
    <Teleport to="body">
      <ModalFormularioV2
        v-model="showCambioCustoModal"
        titulo="Câmbio e Custos — Documento de Entrada"
        tamanho="xxl"
        cor-header="var(--bs-info)"
      >
        <div class="container-fluid">
          <!-- ============================================================ -->
          <!-- SEÇÃO 1: DADOS DE CÂMBIO (igual ao layout da PO)            -->
          <!-- ============================================================ -->

          <!-- Linha de metadados (igual ao título/PO) -->
          <div class="row g-3 mb-4 pb-3 border-bottom">
            <div class="col-md-3">
              <label class="form-label">Tipo Taxa Conversão</label>
              <div class="form-control-plaintext">{{ getTextoLista(listasData.LstTipoTaxaConversao || [], formData?.lstTipoTaxaConversao) }}</div>
            </div>
            <div class="col-md-2">
              <label class="form-label">Data Ref. Câmbio</label>
              <div class="form-control-plaintext">{{ formatarDataBR(formData?.dataReferenciaCambio) }}</div>
            </div>
            <div class="col-md-3">
              <label class="form-label">Fonte da Cotação</label>
              <div class="form-control-plaintext">{{ getTextoLista(listasData.LstFonteCambio || [], formData?.lstFonteCambio) }}</div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Status Taxa</label>
              <div class="form-control-plaintext">{{ getTextoLista(listasData.LstTaxaCambioStatus || [], formData?.lstTaxaCambioStatus) }}</div>
            </div>
          </div>

          <!-- Mini-cards: Moeda Padrão vs Moeda Gerencial (igual à PO) -->
          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <div class="card border-primary h-100">
                <div class="card-header bg-primary text-white py-2">
                  <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Padrão</h6>
                </div>
                <div class="card-body py-2">
                  <div class="row g-2">
                    <div class="col-4">
                      <label class="form-label">Moeda</label>
                      <div class="form-control-plaintext">{{ getTextoLista(listasData.LstMoedaPadrao || [], formData?.lstMoedaPadrao) }}</div>
                    </div>
                    <div class="col-4">
                      <label class="form-label">{{ labelTaxaPadrao }}</label>
                      <div class="form-control-plaintext text-end">{{ formatarValor(formData?.taxaCambioPadrao, 6) }}</div>
                    </div>
                    <div class="col-4">
                      <label class="form-label">Valor Documento</label>
                      <div class="form-control-plaintext text-end">{{ formatarValor(formData?.valorTotalDocumentoPadrao) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="card border-primary h-100">
                <div class="card-header bg-primary text-white py-2">
                  <h6 class="mb-0 text-white small"><i class="bi bi-currency-dollar me-1"></i> Moeda Gerencial</h6>
                </div>
                <div class="card-body py-2">
                  <div class="row g-2">
                    <div class="col-3">
                      <label class="form-label">Moeda</label>
                      <div class="form-control-plaintext">{{ getTextoLista(listasData.LstMoedaGerencial || [], formData?.lstMoedaGerencial) }}</div>
                    </div>
                    <div class="col-3">
                      <label class="form-label">{{ labelTaxaGerencialPadrao }}</label>
                      <div class="form-control-plaintext text-end">{{ formatarValor(formData?.taxaCambioGerencialPadrao, 6) }}</div>
                    </div>
                    <div class="col-3">
                      <label class="form-label">{{ labelTaxaCambioGerencial }}</label>
                      <div class="form-control-plaintext text-end">{{ formatarValor(formData?.taxaCambioGerencial, 6) }}</div>
                    </div>
                    <div class="col-3">
                      <label class="form-label">Valor Documento</label>
                      <div class="form-control-plaintext text-end">{{ formatarValor(formData?.valorTotalDocumentoGerencial) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ============================================================ -->
          <!-- SEÇÃO 2: TOTAIS CONVERTIDOS DO CABEÇALHO (Via A)            -->
          <!-- ============================================================ -->
          <fieldset class="border rounded-2 p-3 mb-3">
            <legend class="fs-6 fw-semibold px-2 w-auto">
              <i class="bi bi-calculator me-1"></i> Totais Convertidos
            </legend>
            <div class="table-responsive">
              <table class="table table-sm table-bordered mb-0">
                <thead class="table-header-custom">
                  <tr>
                    <th>Campo</th>
                    <th class="text-end">Original ({{ getTextoLista(listasData.LstMoeda || [], formData?.lstMoeda) }})</th>
                    <th class="text-end">Moeda Padrão ({{ getTextoLista(listasData.LstMoedaPadrao || [], formData?.lstMoedaPadrao) }})</th>
                    <th class="text-end">Moeda Gerencial ({{ getTextoLista(listasData.LstMoedaGerencial || [], formData?.lstMoedaGerencial) }})</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Valor Total Original</td>
                    <td class="text-end">{{ formatarValor(formData?.valorTotalOriginal) }}</td>
                    <td class="text-end">{{ formatarValor(formData?.valorTotalOriginalPadrao) }}</td>
                    <td class="text-end">{{ formatarValor(formData?.valorTotalOriginalGerencial) }}</td>
                  </tr>
                  <tr>
                    <td>Valor Total Desconto</td>
                    <td class="text-end">{{ formatarValor(formData?.valorTotalDesconto) }}</td>
                    <td class="text-end">{{ formatarValor(formData?.valorTotalDescontoPadrao) }}</td>
                    <td class="text-end">{{ formatarValor(formData?.valorTotalDescontoGerencial) }}</td>
                  </tr>
                  <tr>
                    <td>Valor Total Documento</td>
                    <td class="text-end">{{ formatarValor(formData?.valorTotalDocumento) }}</td>
                    <td class="text-end">{{ formatarValor(formData?.valorTotalDocumentoPadrao) }}</td>
                    <td class="text-end">{{ formatarValor(formData?.valorTotalDocumentoGerencial) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </fieldset>

          <!-- ============================================================ -->
          <!-- SEÇÃO 3: ITENS — VALORES UNITÁRIOS, CUSTO KARDEX E MEMÓRIA  -->
          <!-- ============================================================ -->
          <fieldset class="border rounded-2 p-3">
            <legend class="fs-6 fw-semibold px-2 w-auto">
              <i class="bi bi-list-ul me-1"></i> Itens — Custos e Conversões
            </legend>
            <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-bordered mb-0 align-middle">
                <thead class="table-header-custom" style="position: sticky; top: 0; z-index: 1;">
                  <tr>
                    <th>#</th>
                    <th>Produto</th>
                    <th class="text-end">Qtd</th>
                    <th class="text-end">Preço Unit.</th>
                    <th class="text-end">Preço Unit. Padrão</th>
                    <th class="text-end">Preço Unit. Gerencial</th>
                    <th class="text-end">Custo Kardex</th>
                    <th class="text-end">Custo Kardex Padrão</th>
                    <th class="text-end">Custo Kardex Gerencial</th>
                    <th>Memória de Cálculo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in (formData?.itens || [])" :key="item.idDocumentoEntradaItem || idx">
                    <td class="text-center">{{ idx + 1 }}</td>
                    <td>{{ item.nomeProduto || item.sku || ('Item #' + (idx + 1)) }}</td>
                    <td class="text-end">{{ formatarValor(item.quantidadeUnBasica, 4) }}</td>
                    <td class="text-end">{{ formatarValor(item.precoUnitario, 4) }}</td>
                    <td class="text-end">{{ formatarValor(item.precoUnitarioPadrao, 6) }}</td>
                    <td class="text-end">{{ formatarValor(item.precoUnitarioGerencial, 6) }}</td>
                    <td class="text-end">{{ formatarValor(item.custoKardexUnitario, 6) }}</td>
                    <td class="text-end">{{ formatarValor(item.custoKardexUnitarioPadrao, 6) }}</td>
                    <td class="text-end">{{ formatarValor(item.custoKardexUnitarioGerencial, 6) }}</td>
                    <td style="max-width: 250px; white-space: normal; word-break: break-word; font-size: 0.75rem;">
                      {{ item.memoriaCalculoKardex || '-' }}
                    </td>
                  </tr>
                  <tr v-if="!(formData?.itens?.length)">
                    <td colspan="10" class="text-center text-muted py-3">Nenhum item no documento.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </fieldset>
        </div>

        <template #footer>
          <div class="d-flex gap-2 w-100 justify-content-end">
            <button type="button" class="btn btn-outline-secondary" @click="showCambioCustoModal = false">
              Fechar
            </button>
          </div>
        </template>
      </ModalFormularioV2>
    </Teleport>
  </ModalFormularioV2>
</template>

<style scoped>
.documento-entrada-modal {
  max-width: 100%;
}

.documento-entrada-modal .campo-busca-lista-wrapper {
  margin-bottom: 0;
}

.documento-entrada-modal .campo-busca-lista-wrapper .form-label {
  font-weight: 400 !important;
}

.documento-entrada-modal .campo-busca-lista-wrapper .form-label.is-required::after {
  content: " *";
  color: #dc3545;
  font-weight: bold;
}

/* STEPPER */
.stepper-wrapper {
  border-bottom: 1px solid var(--bs-border-color);
  margin-bottom: 20px;
  padding: 0;
}
.stepper-horizontal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  gap: 4px;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
  position: relative;
  padding: 8px 4px;
  transition: all 0.2s;
  border-radius: 8px;
  user-select: none;
}

.step-item.step-clickable {
  cursor: pointer;
}

.step-item.step-clickable:hover {
  background: var(--bs-tertiary-bg);
}

.step-item.step-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.step-indicator {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
  background: var(--bs-tertiary-bg);
  color: var(--bs-secondary-color);
  border: 2px solid var(--bs-border-color);
}

/* Step ativo: apenas fundo destacado — NÃO altera cores do indicador nem label */
.step-item.step-active {
  background: var(--bs-tertiary-bg);
  border-radius: 8px;
}

.step-item.step-completed .step-indicator {
  background: var(--bs-success);
  color: #fff;
  border-color: var(--bs-success);
}

.step-number {
  font-size: 0.8rem;
}

.step-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-align: center;
  color: var(--bs-secondary-color);
  white-space: nowrap;
}

.step-item.step-completed .step-label {
  color: var(--bs-success);
}

.step-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid var(--bs-border-color);
  margin-top: 16px;
}

.step-counter {
  font-size: 0.8rem;
  color: var(--bs-secondary-color);
  font-weight: 500;
}

.step-content {
  min-height: 200px;
}

/* Grid de parcelas: header e total sensíveis ao tema */
.parcelas-grid thead > tr {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
}
.parcelas-grid thead > tr > th {
  border-bottom: 2px solid var(--bs-border-color);
  font-weight: 600;
  font-size: 0.8rem;
}
.parcelas-grid tfoot .parcelas-total,
.parcelas-grid tfoot tr {
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
}
.parcelas-grid tfoot td {
  border-top: 2px solid var(--bs-border-color);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.bi-arrow-repeat.spin {
  animation: spin 1s linear infinite;
  display: inline-block;
}

/* Headers de tabela sensibilizados ao tema (dark/light) */
.table-header-custom > tr {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
}
.table-header-custom > tr > th {
  border-bottom: 2px solid var(--bs-border-color);
  font-weight: 600;
  font-size: 0.8rem;
}

/* Quando o step Itens está ativo, stepper e navegação
   mantêm a largura de 800px centralizados, só o SmartMode expande */
.stepper-constrained {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}
</style>
